import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import { ReportInaccuracyLink } from '../components/WindowNarrative';
import {
  ArrowLeft, CircleNotch, CalendarBlank, Target, Fire, TrendUp,
  TrendDown, Equals, Lightning, Star, ChartBar, CheckCircle, XCircle, ListNumbers, ArrowRight
} from "@phosphor-icons/react";
import { Toaster, toast } from 'sonner';
import { Button } from '../components/ui/button';
import { syncConstraintsToDB } from '../components/MyPrediction';
import { ExportButton } from '../components/ExportCSV';
import PageConsensusBanner from '../components/PageConsensusBanner';
import BlindSpotsBanner from '../components/BlindSpotsBanner';
import W17DateComponentFrequency from '../components/w17/W17DateComponentFrequency';
import { W17RangeTransitionBlock } from './WindowAccuracy';

const API = process.env.REACT_APP_BACKEND_URL;

const dgColors = [
  "bg-slate-500", "bg-blue-500", "bg-cyan-500", "bg-teal-500", "bg-emerald-500",
  "bg-amber-500", "bg-orange-500", "bg-rose-500", "bg-purple-500", "bg-indigo-500",
];

const matchTypeColors = {
  day: 'bg-blue-100 text-blue-800',
  month: 'bg-green-100 text-green-800',
  year: 'bg-purple-100 text-purple-800',
};

function DateNumberCorrelation({ data }) {
  const s = data.summary;
  const nd = data.next_draw;

  return (
    <div className="space-y-5" data-testid="date-number-correlation">
      {/* Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-base font-bold text-gray-900 mb-1">Date-Number Correlation</h2>
        <p className="text-xs text-gray-400 mb-4">How often do draw date components (day, month, year) appear as ball numbers? Random chance = ~10%</p>

        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-blue-600 uppercase font-bold">Day Match</p>
            <p className="text-2xl font-black text-blue-700 mt-1">{s.day_match.pct}%</p>
            <p className="text-[10px] text-blue-500">{s.day_match.count}/{data.total_draws} draws</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-green-600 uppercase font-bold">Month Match</p>
            <p className="text-2xl font-black text-green-700 mt-1">{s.month_match.pct}%</p>
            <p className="text-[10px] text-green-500">{s.month_match.count}/{data.total_draws} draws</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-purple-600 uppercase font-bold">Year Match</p>
            <p className="text-2xl font-black text-purple-700 mt-1">{s.year_match.pct}%</p>
            <p className="text-[10px] text-purple-500">{s.year_match.count}/{data.total_draws} draws</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-indigo-600 uppercase font-bold">Any Match</p>
            <p className="text-2xl font-black text-indigo-700 mt-1">{s.any_match.pct}%</p>
            <p className="text-[10px] text-indigo-500">{s.any_match.count}/{data.total_draws} draws</p>
          </div>
        </div>
      </div>

      {/* Gap Analysis & CY Comparison */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="gap-analysis">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Gap Analysis & {data.current_year} Comparison</h3>
          <p className="text-[10px] text-gray-400">Expected gap between matches, current streak, and {data.current_year} performance vs expected</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="gap-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2 text-left font-bold text-gray-500">Type</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500">Hist %</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500">Expected Gap</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500">Avg Gap</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500">Max Gap</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500">Draws Absent</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500">Gap Status</th>
                <th className="px-4 py-2 text-center font-bold text-indigo-600">{data.current_year} Actual</th>
                <th className="px-4 py-2 text-center font-bold text-indigo-600">{data.current_year} Expected</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500">Lacking</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Day Number', type: 'day', color: 'text-blue-700', histPct: data.summary.day_match.pct },
                { label: 'Month Number', type: 'month', color: 'text-green-700', histPct: data.summary.month_match.pct },
                { label: 'Year Number', type: 'year', color: 'text-purple-700', histPct: data.summary.year_match.pct },
              ].map(row => {
                const g = data.gap_analysis?.[row.type] || {};
                const overduePct = g.overdue_pct || 0;
                return (
                  <tr key={row.type} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className={`px-4 py-3 font-bold ${row.color}`}>{row.label}</td>
                    <td className="px-4 py-3 text-center font-mono">{row.histPct}%</td>
                    <td className="px-4 py-3 text-center font-mono">every {g.expected_gap} draws</td>
                    <td className="px-4 py-3 text-center font-mono">{g.avg_gap}</td>
                    <td className="px-4 py-3 text-center font-mono">{g.max_gap}</td>
                    <td className={`px-4 py-3 text-center font-mono font-bold ${g.current_absent >= g.expected_gap ? 'text-red-600' : 'text-gray-700'}`}>{g.current_absent}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${overduePct >= 150 ? 'bg-red-100 text-red-700' : overduePct >= 100 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {overduePct >= 100 ? `${overduePct}% overdue` : `${overduePct}% of expected`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-bold text-indigo-700">{g.cy_count}</td>
                    <td className="px-4 py-3 text-center font-mono text-indigo-500">{g.cy_expected}</td>
                    <td className={`px-4 py-3 text-center font-mono font-bold ${g.cy_diff < -0.5 ? 'text-red-600' : g.cy_diff > 0.5 ? 'text-blue-600' : 'text-gray-400'}`}>
                      {g.cy_diff > 0 ? '+' : ''}{g.cy_diff}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Next Draw */}
      <div className="bg-gradient-to-r from-[#002FA7] to-[#001d6a] rounded-lg p-5 text-white" data-testid="next-draw-hint">
        <p className="text-xs text-white/60 uppercase font-bold">Next Draw: {nd.date}</p>
        <div className="grid grid-cols-3 gap-4 mt-3">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-[10px] text-white/50">Day</p>
            <p className="text-3xl font-black">{nd.day}</p>
            <p className="text-[10px] text-white/40">11% historical match</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-[10px] text-white/50">Month</p>
            <p className="text-3xl font-black">{nd.month}</p>
            <p className="text-[10px] text-white/40">9.7% historical match</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <p className="text-[10px] text-white/50">Year (last 2)</p>
            <p className="text-3xl font-black">{nd.year_short}</p>
            <p className="text-[10px] text-white/40">10.8% historical match</p>
          </div>
        </div>
      </div>

      {/* Per-Position Rates */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Match Rate Per Position</h3>
          <p className="text-[10px] text-gray-400">How often each date component matches at each ball position. Expected random = ~2% per position.</p>
        </div>        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="position-rates-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2 text-left font-bold text-gray-500">Position</th>
                <th className="px-4 py-2 text-center font-bold text-blue-600">Day Count</th>
                <th className="px-4 py-2 text-center font-bold text-blue-600">Day %</th>
                <th className="px-4 py-2 text-center font-bold text-green-600">Month Count</th>
                <th className="px-4 py-2 text-center font-bold text-green-600">Month %</th>
                <th className="px-4 py-2 text-center font-bold text-purple-600">Year Count</th>
                <th className="px-4 py-2 text-center font-bold text-purple-600">Year %</th>
              </tr>
            </thead>
            <tbody>
              {data.position_rates.map((r, i) => (
                <tr key={r.ball_key} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                  <td className="px-4 py-2.5 font-bold text-gray-900">{r.position}</td>
                  <td className="px-4 py-2.5 text-center font-mono">{r.day_count}</td>
                  <td className={`px-4 py-2.5 text-center font-mono font-bold ${r.day_pct > 3 ? 'text-blue-700 bg-blue-50' : 'text-gray-500'}`}>{r.day_pct}%</td>
                  <td className="px-4 py-2.5 text-center font-mono">{r.month_count}</td>
                  <td className={`px-4 py-2.5 text-center font-mono font-bold ${r.month_pct > 3 ? 'text-green-700 bg-green-50' : 'text-gray-500'}`}>{r.month_pct}%</td>
                  <td className="px-4 py-2.5 text-center font-mono">{r.year_count}</td>
                  <td className={`px-4 py-2.5 text-center font-mono font-bold ${r.year_pct > 3 ? 'text-purple-700 bg-purple-50' : 'text-gray-500'}`}>{r.year_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Matches */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Recent Draws with Date Matches</h3>
          <p className="text-[10px] text-gray-400">{data.total_matching_draws} draws had at least one date component match. Showing latest 30.</p>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="recent-matches-table">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-bold text-gray-500">Draw #</th>
                <th className="px-3 py-2 text-left font-bold text-gray-500">Date</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B1</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B2</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B3</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B4</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B5</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">LS</th>
                <th className="px-3 py-2 text-left font-bold text-gray-500">Matches</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_matches.map((d) => {
                const matchNums = new Set(d.matches.map(m => m.value));
                const matchPositions = {};
                d.matches.forEach(m => { matchPositions[m.position] = m.type; });
                return (
                  <tr key={d.draw_number} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono font-bold">{d.draw_number}</td>
                    <td className="px-3 py-2 text-gray-600">{d.draw_date}</td>
                    {['ball_1','ball_2','ball_3','ball_4','ball_5'].map((bk, j) => {
                      const v = d.balls[j];
                      const mType = matchPositions[bk];
                      return (
                        <td key={j} className={`px-3 py-2 text-center font-mono font-bold ${mType ? matchTypeColors[mType] + ' rounded' : ''}`}>
                          {v}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center font-mono text-gray-400">{d.lucky_stars.join(', ')}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 flex-wrap">
                        {d.matches.map((m, i) => (
                          <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${matchTypeColors[m.type]}`}>
                            {m.type}={m.value} ({m.position.replace('ball_', 'B')})
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly Breakdown */}
      {data.monthly_breakdown && data.monthly_breakdown.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="monthly-breakdown">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-bold text-gray-900">Monthly Breakdown</h3>
            <p className="text-[10px] text-gray-400">
              Out of {s.month_match.count} total month-number matches — how many came from each month?
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="monthly-breakdown-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2 text-left font-bold text-gray-500">Month</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Number</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Matches</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Share</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Last Hit</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Absent</th>
                  <th className="px-4 py-2 text-left font-bold text-gray-500">Distribution</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const maxAbsent = Math.max(...data.monthly_breakdown.map(m => m.years_absent || 0));
                  return data.monthly_breakdown.map((mb) => {
                    const share = s.month_match.count > 0 ? ((mb.matches / s.month_match.count) * 100).toFixed(1) : 0;
                    const barW = s.month_match.count > 0 ? (mb.matches / Math.max(...data.monthly_breakdown.map(m => m.matches), 1)) * 100 : 0;
                    const isLongestMissing = mb.years_absent > 0 && mb.years_absent === maxAbsent;
                    return (
                      <tr key={mb.month} className={`border-b border-gray-100 hover:bg-gray-50 ${isLongestMissing ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-2.5 font-bold text-gray-800">
                          {mb.month_name}
                          {isLongestMissing && <span className="ml-1.5 text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">LONGEST MISSING</span>}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-flex w-7 h-7 rounded-full items-center justify-center bg-green-600 text-white text-xs font-bold">{mb.number}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center font-mono font-bold text-gray-900">{mb.matches}</td>
                        <td className="px-4 py-2.5 text-center font-mono text-gray-600">{share}%</td>
                        <td className="px-4 py-2.5 text-center font-mono text-gray-700">{mb.last_year || '—'}</td>
                        <td className={`px-4 py-2.5 text-center font-mono font-bold ${mb.years_absent >= 2 ? 'text-red-600' : mb.years_absent >= 1 ? 'text-amber-600' : 'text-green-600'}`}>
                          {mb.years_absent === 0 ? 'This year' : `${mb.years_absent}yr`}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="w-full bg-gray-100 rounded-full h-3">
                            <div className="h-3 rounded-full bg-green-500" style={{ width: `${barW}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-300">
                  <td className="px-4 py-2 font-bold text-gray-700">Total</td>
                  <td />
                  <td className="px-4 py-2 text-center font-mono font-black text-gray-900">{s.month_match.count}</td>
                  <td className="px-4 py-2 text-center font-mono text-gray-600">100%</td>
                  <td />
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WindowCalendarPatterns() {
  const navigate = useNavigate();
  const [cpData, setCpData] = useState(null);
  const [ddData, setDdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState("consecutive");
  const [mainView, setMainView] = useState("calendar");
  const [corrData, setCorrData] = useState(null);
  const [corrLoading, setCorrLoading] = useState(false);
  const [annivData, setAnnivData] = useState(null);

  // fetchData reads selectedDate only to seed the initial value if missing — using
  // the functional setter form keeps this stable so the effect below stays mount-only.
  const fetchData = useCallback((day, month) => {
    setLoading(true);
    const cpParams = day && month ? `?day=${day}&month=${month}` : "";
    const ddParams = day && month ? `?day=${day}&month=${month}` : "";
    Promise.all([
      axios.get(`${API}/api/analysis/calendar-patterns${cpParams}`),
      axios.get(`${API}/api/analysis/date-digit-analysis${ddParams}`),
    ]).then(([cpRes, ddRes]) => {
      setCpData(cpRes.data);
      setDdData(ddRes.data);
      if (cpRes.data.day && cpRes.data.month) {
        setSelectedDate(prev => prev || { day: cpRes.data.day, month: cpRes.data.month });
      }
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  useEffect(() => {
    axios.get(`${API}/api/analysis/anniversary`)
      .then(res => setAnnivData(res.data))
      .catch(() => {});
  }, []);

  const fetchCorrelation = useCallback(() => {
    setCorrLoading(true);
    axios.get(`${API}/api/analysis/date-number-correlation`)
      .then(res => setCorrData(res.data))
      .catch(() => {})
      .finally(() => setCorrLoading(false));
  }, []);

  useEffect(() => {
    if (mainView === 'correlation' && !corrData) fetchCorrelation();
  }, [mainView, corrData, fetchCorrelation]);

  const handleDateChange = (val) => {
    const [d, m] = val.split("-").map(Number);
    setSelectedDate({ day: d, month: m });
    fetchData(d, m);
  };

  if (loading && !cpData) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="calendar-loading">
      <CircleNotch size={40} className="animate-spin text-blue-600" />
    </div>
  );

  const data = cpData;
  const digit = ddData;
  if (!data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-500">Failed to load data</div>
    </div>
  );

  const tabs = [
    { id: "consecutive", label: "🔁 Consecutive Draws" },
    { id: "overview", label: "📅 Same-Date Overview" },
    { id: "transitions", label: "Range Transitions" },
    { id: "digit-groups", label: "Digit Groups" },
    { id: "overlap", label: "Overlap" },
    { id: "date-components", label: "📆 Date / Month / Year Numbers" },
  ];

  const nextDrawLabel = digit?.next_draw_date ? `Next draw: ${digit.next_draw_date}` : "";

  return (
    <div className="min-h-screen bg-gray-50" data-testid="calendar-patterns-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-gray-500 hover:text-gray-900" data-testid="back-button">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">Window 17 — Comparison to Previous Draws</h1>
            <p className="text-xs text-gray-500">
              {nextDrawLabel}{nextDrawLabel ? " — " : ""}Consecutive-draw replication + Same-Date structural patterns across years
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CalendarBlank size={18} className="text-gray-400" />
            <select
              value={selectedDate ? `${selectedDate.day}-${selectedDate.month}` : ""}
              onChange={e => handleDateChange(e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1.5 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="date-selector"
            >
              {data.available_dates?.map(d => (
                <option key={`${d.day}-${d.month}`} value={`${d.day}-${d.month}`}>{d.label}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6 space-y-5">
        {/* === INTRODUCTION + OBSERVATIONS — side by side === */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="w17-intro-obs-grid">
          <W17Introduction
            nextDay={annivData?.next_day}
            nextMonth={annivData?.next_month}
            totalDraws={annivData?.total_draws}
            sameDateDraws={annivData?.same_date_draws}
            sameMonthDraws={annivData?.same_month_draws}
            monthName={data?.month_name}
          />
          <W17Observations annivData={annivData} />
        </div>

        {/* === KEY FINDINGS — applicable to next draw === */}
        <W17KeyFindingsForNextDraw annivData={annivData} />

        {/* View Toggle */}
        <div className="flex gap-2 border-b border-gray-200 pb-0" data-testid="calendar-view-toggle">
          <button
            onClick={() => setMainView('calendar')}
            className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${mainView === 'calendar' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="view-calendar"
          >
            Calendar Pattern Analysis
          </button>
          <button
            onClick={() => setMainView('correlation')}
            className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${mainView === 'correlation' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="view-correlation"
          >
            Date-Number Correlation
          </button>
        </div>

        {mainView === 'correlation' ? (
          corrLoading ? (
            <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
          ) : corrData ? (
            <DateNumberCorrelation data={corrData} />
          ) : <div className="text-center py-10 text-gray-400">Loading...</div>
        ) : (
        <>
        {loading && (
          <div className="flex justify-center py-4">
            <CircleNotch size={24} className="animate-spin text-blue-600" />
          </div>
        )}

        {!loading && data.matched_count < 2 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <p className="text-amber-800 font-medium">Only {data.matched_count} draw(s) found for {data.month_name} {data.day}. Need at least 2 for pattern analysis.</p>
          </div>
        )}

        {!loading && data.matched_count >= 2 && (
          <>
            {/* Header Card */}
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white" data-testid="header-card">
              <div className="flex items-center gap-3 mb-3">
                <CalendarBlank size={28} weight="fill" />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">{data.month_name} {data.day}</h2>
                  <p className="text-indigo-200 text-sm">{data.matched_count} draws across {data.draws[0]?.year}–{data.draws[data.draws.length - 1]?.year}</p>
                </div>
                {data.pattern_strength && (
                  <div className="text-right" data-testid="pattern-strength">
                    <div className={`text-3xl font-black ${data.pattern_strength.score >= 75 ? 'text-emerald-300' : data.pattern_strength.score >= 50 ? 'text-yellow-300' : data.pattern_strength.score >= 30 ? 'text-orange-300' : 'text-red-300'}`}>
                      {data.pattern_strength.score}
                    </div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-200">{data.pattern_strength.label}</div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {data.signals?.filter(s => s.strength === "HIGH").map((sig, i) => (
                  <span key={i} className="bg-white/20 backdrop-blur rounded-full px-3 py-1 text-xs font-medium" data-testid={`signal-badge-${i}`}>
                    {sig.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 overflow-x-auto" data-testid="tab-bar">
              {tabs.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === t.id ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                  data-testid={`tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {activeTab === "consecutive" && <ConsecutiveDrawsTab />}
            {activeTab === "overview" && <OverviewTab data={data} digit={digit} />}
            {activeTab === "transitions" && <TransitionsTab data={data} />}
            {activeTab === "digit-groups" && <DigitGroupsTab digit={digit} />}
            {activeTab === "overlap" && <OverlapTab digit={digit} />}
            {activeTab === "date-components" && (
              <W17DateComponentFrequency
                nextDay={annivData?.next_day}
                nextMonth={annivData?.next_month}
                nextYearShort={annivData?.next_draw_date ? Number((annivData.next_draw_date.split('/')[2] || '').slice(-2)) : null}
              />
            )}
          </>
        )}
        </>
        )}
        {/* Anniversary: Same Date + Same Month per-position frequency tables
            Rendered only on the "🔁 Consecutive Draws" tab per user request —
            previously appeared on every tab because it sat outside the
            activeTab conditional. */}
        {annivData && activeTab === "consecutive" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6" data-testid="anniversary-panels">
            <PerPositionFrequencyTable
              title={`Same Date — Day ${annivData.next_day} of any month, any year`}
              subtitle={`${annivData.same_date_draws} historical draws · per-position counts for numbers 1-50 (LS columns filled for 1-12)`}
              accent="from-amber-600 to-orange-600"
              ballRows={annivData.same_date_per_position_balls || []}
              testId="anniv-same-date-table"
            />
            <PerPositionFrequencyTable
              title={`Same Month — Month ${annivData.next_month} (any day, any year)`}
              subtitle={`${annivData.same_month_draws} historical draws · per-position counts for numbers 1-50 (LS columns filled for 1-12)`}
              accent="from-indigo-700 to-blue-800"
              ballRows={annivData.same_month_per_position_balls || []}
              testId="anniv-same-month-table"
            />
          </div>
        )}
      </main>
    </div>
  );
}

/* ========== INTRODUCTION BLOCK (top of page) ========== */
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function W17Introduction({ nextDay, nextMonth, totalDraws, sameDateDraws, sameMonthDraws, monthName }) {
  const monthLabel = nextMonth ? MONTH_NAMES[(nextMonth - 1) % 12] : (monthName || '—');
  const dayLabel = nextDay ?? '—';

  return (
    <div className="bg-gradient-to-r from-sky-50 via-blue-50 to-indigo-50 border-2 border-sky-300 rounded-xl p-5" data-testid="w17-introduction">      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-sky-600 text-white items-center justify-center font-black text-base">i</div>
        <div className="flex-1">
          <h2 className="text-base font-black text-sky-900 uppercase tracking-wider">Introduction — What this window analyses</h2>
          <p className="text-sm text-sky-800 mt-1.5 mb-3">
            Window 17 cross-checks the upcoming draw against history along <strong>four independent dimensions</strong>:
          </p>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-800 marker:font-black marker:text-sky-700">
            <li>
              <strong>Same numbers repeating from the previous draw</strong> — for Ball 1 → Ball 5, how often does any number from the previous draw reappear in the very next draw?
            </li>
            <li>
              <strong>Streak of repetition</strong> — when one number repeats, does it carry over for 2, 3, 4 or more consecutive draws? Longest historical streaks vs current streak.
            </li>
            <li>
              <strong>Numbers under-represented on the same date</strong> — Day {dayLabel} of any month, any year across {sameDateDraws ?? '—'} historical draws. Identifies "cold" numbers that rarely appear on this calendar day.
            </li>
            <li>
              <strong>Numbers under-represented in the same month</strong> — {monthLabel} of any year across {sameMonthDraws ?? '—'} historical draws. Identifies numbers structurally avoided in this month over {totalDraws ? `${totalDraws} total draws` : '22 years of draws'}.
            </li>
          </ol>
          <p className="text-xs text-sky-700 mt-3 italic">
            Each section below tests one of these dimensions against the next draw ({monthLabel} {dayLabel}). Scroll to the bottom for the auto-generated <strong>Observations</strong> block summarising key findings.
          </p>
          <ReportInaccuracyLink block="introduction" windowId="W17" accentColor="text-sky-700 hover:text-sky-900" />
        </div>
      </div>
    </div>
  );
}

/* ========== OBSERVATIONS BLOCK (bottom of page) ========== */
function W17Observations({ annivData }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/analysis/pattern-replication`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, []);

  if (!data && !annivData) return null;

  const sn = data?.shared_numbers || [];
  const noRepeat = sn.find(s => s.shared === 0);
  const oneShared = sn.find(s => s.shared === 1);
  const twoShared = sn.find(s => s.shared === 2);
  const threePlus = sn.filter(s => s.shared >= 3).reduce((a, b) => a + (b.pct || 0), 0);

  const sa = data?.streak_analysis || {};
  const longest = sa.longest_streaks?.shared_ge_1?.longest_streak;
  const longest2 = sa.longest_streaks?.shared_ge_2?.longest_streak;
  const totalPairs = sa.total_pairs_analysed;

  // Anniversary observations
  const sdBalls = annivData?.same_date_per_position_balls || [];
  const smBalls = annivData?.same_month_per_position_balls || [];
  const sdDraws = annivData?.same_date_draws || 0;
  const smDraws = annivData?.same_month_draws || 0;

  // Top under-represented (lowest total) for same-date and same-month
  const sdRare = [...sdBalls].sort((a, b) => (a.total || 0) - (b.total || 0)).slice(0, 5);
  const sdHot = [...sdBalls].sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5);
  const smRare = [...smBalls].sort((a, b) => (a.total || 0) - (b.total || 0)).slice(0, 5);
  const smHot = [...smBalls].sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5);
  const monthLabel = annivData?.next_month ? MONTH_NAMES[(annivData.next_month - 1) % 12] : '—';
  const dayLabel = annivData?.next_day ?? '—';

  return (
    <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-400 rounded-xl p-5 mt-6" data-testid="w17-observations">
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-amber-600 text-white items-center justify-center font-black text-base">!</div>
        <div className="flex-1">
          <h2 className="text-base font-black text-amber-900 uppercase tracking-wider">Observations — Key findings</h2>
          <p className="text-sm text-amber-800 mt-1.5 mb-3">
            Auto-generated insights from the four dimensions above, computed across {totalPairs ? `${totalPairs.toLocaleString()} consecutive-draw pairs` : 'the full draw history'}.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-900 marker:font-black marker:text-amber-700">
            {noRepeat && (
              <li>
                <strong>{noRepeat.pct}%</strong> of draws have <strong>no repeating numbers</strong> from the previous draw ({noRepeat.count.toLocaleString()} / {totalPairs?.toLocaleString() || '—'} pairs). Repetition is the exception, not the rule.
              </li>
            )}
            {oneShared && (
              <li>
                Exactly <strong>1 number repeats</strong> in <strong>{oneShared.pct}%</strong> of cases ({oneShared.count.toLocaleString()} pairs).
              </li>
            )}
            {twoShared && (
              <li>
                <strong>2 numbers repeat</strong> in only <strong>{twoShared.pct}%</strong> of cases — a meaningful but rare event ({twoShared.count.toLocaleString()} pairs).
              </li>
            )}
            {threePlus > 0 && (
              <li>
                <strong>3 or more numbers repeating</strong> happens in just <strong>{threePlus.toFixed(2)}%</strong> of draws — extraordinary; if you see one, it tends to break immediately (next draw averages <strong>{sa.high_share_followup?.avg_next_shared ?? '—'}</strong> shared).
              </li>
            )}
            {longest != null && (
              <li>
                The <strong>longest ever streak</strong> with at least 1 number repeating is <strong>{longest} consecutive draws</strong>. With ≥2 numbers repeating, the longest streak ever observed is <strong>{longest2 ?? '—'}</strong>.
              </li>
            )}
            {sa.current_streak_ge_1 != null && (
              <li>
                <strong>Current streak</strong>: {sa.current_streak_ge_1} draw(s) in a row with ≥1 repeating number{sa.current_streak_ge_2 > 0 ? `, ${sa.current_streak_ge_2} with ≥2 repeating` : ''}.
              </li>
            )}
            {sdRare.length > 0 && sdDraws > 0 && (
              <li>
                <strong>Same-date cold numbers</strong> (Day {dayLabel} of any month, {sdDraws} historical draws): the most under-represented are <strong>{sdRare.map(r => `${r.number} (×${r.total || 0})`).join(', ')}</strong>. These have rarely or never appeared on day {dayLabel} in any position.
              </li>
            )}
            {sdHot.length > 0 && sdDraws > 0 && (
              <li>
                <strong>Same-date hot numbers</strong>: <strong>{sdHot.map(r => `${r.number} (×${r.total || 0})`).join(', ')}</strong> — most frequently drawn on day {dayLabel} historically.
              </li>
            )}
            {smRare.length > 0 && smDraws > 0 && (
              <li>
                <strong>Same-month cold numbers</strong> ({monthLabel} of any year, {smDraws} historical draws): under-represented are <strong>{smRare.map(r => `${r.number} (×${r.total || 0})`).join(', ')}</strong>.
              </li>
            )}
            {smHot.length > 0 && smDraws > 0 && (
              <li>
                <strong>Same-month hot numbers</strong>: <strong>{smHot.map(r => `${r.number} (×${r.total || 0})`).join(', ')}</strong> — most active in {monthLabel} historically.
              </li>
            )}
          </ol>
          <p className="text-xs text-amber-800 mt-3 italic">
            Bold percentages are the high-conviction takeaways. Numbers in the cold lists may either be permanently structurally avoided OR overdue — combine with W11 (Positional Hot/Cold) and W21 (Decay Frequency) to pick which.
          </p>
          <ReportInaccuracyLink block="observations" windowId="W17" accentColor="text-amber-700 hover:text-amber-900" />
        </div>
      </div>
    </div>
  );
}

/* ========== KEY FINDINGS — applicable to next draw ========== */
function W17KeyFindingsForNextDraw({ annivData }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/analysis/pattern-replication`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, []);

  if (!data && !annivData) return null;

  const last20 = data?.last_20 || [];
  const sn = data?.shared_numbers || [];
  const longTermNoRepeat = sn.find(s => s.shared === 0)?.pct || 57.7;
  const longTermRepeatRate = 100 - longTermNoRepeat;
  const sa = data?.streak_analysis || {};

  // ===== 1) Streak heating analysis =====
  // Look at last 5 + last 10 draws for share-rate trend
  const last5 = last20.slice(0, 5);
  const last10 = last20.slice(0, 10);
  const last5Share = last5.length > 0 ? last5.filter(d => (d.shared_with_prev || 0) >= 1).length / last5.length * 100 : 0;
  const last10Share = last10.length > 0 ? last10.filter(d => (d.shared_with_prev || 0) >= 1).length / last10.length * 100 : 0;
  const last5MultiShare = last5.filter(d => (d.shared_with_prev || 0) >= 2).length;
  const lastDrawShared = last20[0]?.shared_with_prev ?? null;

  const heatingDelta = last5Share - longTermRepeatRate;
  let streakVerdict, streakIcon, streakColor;
  if (heatingDelta > 15) {
    streakVerdict = "HEATING UP";
    streakIcon = "🔥";
    streakColor = "from-red-500 to-orange-600";
  } else if (heatingDelta > 5) {
    streakVerdict = "MILDLY ELEVATED";
    streakIcon = "🌡️";
    streakColor = "from-orange-500 to-amber-500";
  } else if (heatingDelta < -15) {
    streakVerdict = "COOLING DOWN";
    streakIcon = "🧊";
    streakColor = "from-blue-500 to-cyan-600";
  } else {
    streakVerdict = "NORMAL";
    streakIcon = "📊";
    streakColor = "from-slate-500 to-slate-600";
  }

  // ===== 2) Cross-cohort hot/cold =====
  const sdBalls = annivData?.same_date_per_position_balls || [];
  const smBalls = annivData?.same_month_per_position_balls || [];
  const sdMap = Object.fromEntries(sdBalls.map(r => [r.number, r.total || 0]));
  const smMap = Object.fromEntries(smBalls.map(r => [r.number, r.total || 0]));

  const sdHotSet = new Set([...sdBalls].sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 10).map(r => r.number));
  const smHotSet = new Set([...smBalls].sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 10).map(r => r.number));
  const sdColdSet = new Set([...sdBalls].sort((a, b) => (a.total || 0) - (b.total || 0)).slice(0, 10).map(r => r.number));
  const smColdSet = new Set([...smBalls].sort((a, b) => (a.total || 0) - (b.total || 0)).slice(0, 10).map(r => r.number));

  // Numbers in BOTH same-date hot AND same-month hot
  const dualHot = [...sdHotSet].filter(n => smHotSet.has(n)).sort((a, b) => (smMap[b] + sdMap[b]) - (smMap[a] + sdMap[a]));
  // Numbers in BOTH cold lists
  const dualCold = [...sdColdSet].filter(n => smColdSet.has(n)).sort((a, b) => (sdMap[a] + smMap[a]) - (sdMap[b] + smMap[b]));

  // ===== 3) Numbers from last draw that are also dual-hot (likely to repeat) =====
  const lastDrawBalls = last20[0]?.balls || [];
  const lastDrawDualHot = lastDrawBalls.filter(n => sdHotSet.has(n) && smHotSet.has(n));

  // ===== 4) Predicted repetition for next draw =====
  let predictionLabel, predictionDetail;
  if (last5MultiShare >= 2 || (last5Share > longTermRepeatRate + 20)) {
    predictionLabel = "UNPREDICTABLE — recent draws breaking the long-term pattern";
    predictionDetail = `Recent ${last5.length} draws show ${last5Share.toFixed(0)}% repetition rate vs ${longTermRepeatRate.toFixed(0)}% long-term.`;
  } else if (lastDrawShared !== null && lastDrawShared >= 2) {
    predictionLabel = "EXPECT REGRESSION — last draw repeated 2+ numbers, next draw averages 0.5 shared";
    predictionDetail = `Historically when ≥3 numbers repeat, the streak breaks immediately (50% no-repeat in next draw).`;
  } else {
    predictionLabel = "STANDARD — repetition pattern in line with long-term average";
    predictionDetail = `Last 5 draws share rate: ${last5Share.toFixed(0)}% vs ${longTermRepeatRate.toFixed(0)}% long-term. Most likely outcome: 0 or 1 number repeats.`;
  }

  const monthLabel = annivData?.next_month ? MONTH_NAMES[(annivData.next_month - 1) % 12] : '—';
  const dayLabel = annivData?.next_day ?? '—';
  const nextDrawDate = annivData?.next_draw_date || `${dayLabel} ${monthLabel}`;

  return (
    <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-500 rounded-xl p-5" data-testid="w17-key-findings">
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-emerald-600 text-white items-center justify-center font-black text-base">★</div>
        <div className="flex-1">
          <h2 className="text-base font-black text-emerald-900 uppercase tracking-wider">Key Findings — applicable to the draw on {nextDrawDate}</h2>
          <p className="text-sm text-emerald-800 mt-1.5 mb-3">
            Synthesised insights combining the historical observations above with the most recent {last20.length} draws.
          </p>

          {/* Streak status badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${streakColor} text-white font-bold text-sm mb-4 shadow-md`} data-testid="streak-verdict-badge">
            <span className="text-base">{streakIcon}</span>
            <span>Streak status: {streakVerdict}</span>
            <span className="text-xs opacity-90 font-mono">({last5Share.toFixed(0)}% recent vs {longTermRepeatRate.toFixed(0)}% long-term)</span>
          </div>

          <ol className="list-decimal list-inside space-y-2.5 text-sm text-gray-900 marker:font-black marker:text-emerald-700">
            <li>
              <strong>Streak temperature</strong>:&nbsp;
              {heatingDelta > 5 ? (
                <>Compared to history, this time streak is <strong className="text-red-700">heating</strong>.&nbsp;</>
              ) : heatingDelta < -5 ? (
                <>Compared to history, this time streak is <strong className="text-blue-700">cooling</strong>.&nbsp;</>
              ) : (
                <>Streak frequency is <strong>matching the historical baseline</strong> ({longTermRepeatRate.toFixed(0)}% repeat rate).&nbsp;</>
              )}
              In the last 5 draws, <strong>{last5.filter(d => (d.shared_with_prev || 0) >= 1).length}</strong> of them shared <strong>≥1 number</strong> with the prior draw
              {last5MultiShare > 0 && <>, and <strong>{last5MultiShare}</strong> shared <strong>≥2 numbers</strong></>}
              {lastDrawShared !== null && (
                <> · <strong>last draw shared {lastDrawShared} number{lastDrawShared !== 1 ? 's' : ''}</strong> with the previous draw</>
              )}
              . <em className="text-emerald-800">→ {predictionLabel}.</em>
              <div className="text-xs text-gray-600 mt-1 ml-6">{predictionDetail}</div>
            </li>

            {dualHot.length > 0 && (
              <li>
                <strong>Cross-cohort HOT</strong> — these numbers are popular both on day {dayLabel} of any month <em>and</em> in {monthLabel} of any year:&nbsp;
                {dualHot.slice(0, 8).map((n, i) => (
                  <span key={n} className="inline-flex items-center gap-1 mx-0.5">
                    <span className="inline-flex w-7 h-7 rounded-full bg-emerald-500 text-white items-center justify-center font-mono font-black text-xs shadow">{n}</span>
                    <span className="text-[10px] text-gray-500 font-mono">(d:{sdMap[n]} m:{smMap[n]})</span>
                    {i < Math.min(dualHot.length - 1, 7) ? ',' : ''}
                  </span>
                ))}
                . <em className="text-emerald-800">→ Strong dual-signal candidates for inclusion in next-draw tickets.</em>
              </li>
            )}

            {lastDrawDualHot.length > 0 && (
              <li>
                <strong>Likely repeat candidates</strong> — numbers in the last drawn set that are <em>also</em> cross-cohort HOT (top dual-hot intersection):&nbsp;
                {lastDrawDualHot.map(n => (
                  <span key={n} className="inline-flex w-7 h-7 mx-0.5 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-black text-xs shadow ring-2 ring-amber-300 align-middle">{n}</span>
                ))}.
                <em className="text-emerald-800"> → Higher-than-baseline chance these specific numbers reappear in the next draw.</em>
              </li>
            )}

            {dualCold.length > 0 && (
              <li>
                <strong>Cross-cohort COLD</strong> — structurally avoided in BOTH same-date and same-month cohorts:&nbsp;
                {dualCold.slice(0, 8).map((n, i) => (
                  <span key={n} className="inline-flex items-center gap-1 mx-0.5">
                    <span className="inline-flex w-7 h-7 rounded-full bg-slate-500 text-white items-center justify-center font-mono font-black text-xs">{n}</span>
                    <span className="text-[10px] text-gray-500 font-mono">(d:{sdMap[n]} m:{smMap[n]})</span>
                    {i < Math.min(dualCold.length - 1, 7) ? ',' : ''}
                  </span>
                ))}
                . <em className="text-emerald-800">→ Either persistent dead-zones to avoid, OR significantly overdue for {monthLabel} {dayLabel}.</em>
              </li>
            )}

            {sa.current_streak_ge_1 >= 3 && (
              <li>
                <strong>Active multi-draw streak</strong>: {sa.current_streak_ge_1} consecutive draws have shared ≥1 number with their predecessor.
                <em className="text-emerald-800"> → Pure mean reversion suggests the streak is statistically due to break (only {sn.find(s => s.shared === 0)?.pct || 57.7}% of draws break the chain).</em>
              </li>
            )}
          </ol>

          <p className="text-xs text-emerald-700 mt-3 italic">
            All findings here are derived live from the data above. Use the dual-hot picks as core ticket numbers and combine with W11/W21 momentum signals for the strongest play.
          </p>
          <ReportInaccuracyLink block="key-findings" windowId="W17" accentColor="text-emerald-700 hover:text-emerald-900" />
        </div>
      </div>
    </div>
  );
}

/* ========== OVERVIEW TAB ========== */
function OverviewTab({ data, digit }) {
  const { draws } = data;
  return (
    <div className="space-y-5">
      {/* Draw History Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="draw-history">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-bold text-gray-700">Draw History on {data.month_name} {data.day}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                <th className="px-3 py-2 text-left">Year</th>
                <th className="px-3 py-2 text-center">Balls</th>
                <th className="px-3 py-2 text-center">LS</th>
                <th className="px-3 py-2 text-center">Sum</th>
                <th className="px-3 py-2 text-center">Spread</th>
                <th className="px-3 py-2 text-center">O/E</th>
                <th className="px-3 py-2 text-center">H/L</th>
                {digit && <th className="px-3 py-2 text-center">Digit Grps</th>}
              </tr>
            </thead>
            <tbody>
              {draws.map((dr, i) => {
                const isLatest = i === draws.length - 1;
                const digitDetail = digit?.draw_details?.find(dd => dd.year === dr.year);
                return (
                  <tr key={i} className={`border-t border-gray-100 ${isLatest ? "bg-indigo-50" : ""}`} data-testid={`draw-row-${dr.year}`}>
                    <td className="px-3 py-2 font-bold text-gray-900">
                      {dr.year} {isLatest && <span className="text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded ml-1">LATEST</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {dr.balls.map((b, j) => (
                          <span key={j} className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold">{b}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {dr.ls.map((l, j) => (
                          <span key={j} className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-500 text-white text-xs font-bold">{l}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center font-mono">{dr.sum}</td>
                    <td className="px-3 py-2 text-center font-mono">{dr.spread}</td>
                    <td className="px-3 py-2 text-center"><span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100">{dr.odds}O/{dr.evens}E</span></td>
                    <td className="px-3 py-2 text-center"><span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100">{dr.highs}H/{dr.lows}L</span></td>
                    {digit && (
                      <td className="px-3 py-2 text-center">
                        <div className="flex gap-0.5 justify-center">
                          {digitDetail?.ball_digit_groups?.map((dg, j) => (
                            <span key={j} className="inline-flex w-5 h-5 rounded items-center justify-center bg-gray-100 text-gray-700 font-mono text-[10px] font-bold">{dg}</span>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Repeated Numbers from structural analysis */}
      {data.repeated_numbers && data.repeated_numbers.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg" data-testid="repeated-numbers">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-700">Repeated Numbers (Balls Only)</h3>
          </div>
          <div className="p-4 flex flex-wrap gap-3">
            {data.repeated_numbers.map((rep, i) => (
              <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2" data-testid={`repeat-${rep.number}`}>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold">{rep.number}</span>
                <div><p className="text-xs font-bold text-gray-800">{rep.count}x</p><p className="text-[10px] text-gray-400">{rep.years.join(", ")}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== RANGE TRANSITIONS TAB ========== */
function TransitionsTab({ data }) {
  const rt = data.range_transitions;

  // Backtest accuracy fetched from the W50 window-accuracy endpoint (which
  // now exposes `w17_range_transition_accuracy`). We fetch lazily on mount
  // so the tab renders instantly and the backtest block appears once the
  // data lands. On failure the block simply doesn't show.
  const [backtest, setBacktest] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/analysis/window-accuracy`);
        if (!cancelled) setBacktest(res.data?.w17_range_transition_accuracy || null);
      } catch { /* leave empty; the block is optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!rt) return <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">No transition data</div>;

  const positions = ["ball_1", "ball_2", "ball_3", "ball_4", "ball_5"];
  const posLabels = { ball_1: "Ball 1", ball_2: "Ball 2", ball_3: "Ball 3", ball_4: "Ball 4", ball_5: "Ball 5" };

  return (
    <div className="space-y-5">
      {/* Range Transition backtest — projected-vs-actual accuracy for the
          last 55 draws. Sits at the top of the tab so users immediately
          see how well the projection has performed historically. */}
      {backtest && (
        <W17RangeTransitionBlock rt={backtest} onGoTo={() => {/* already on W17 */}} />
      )}
      {/* Block 0 — Explanation */}
      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="rt-block-0">
        <header className="bg-slate-800 text-white px-4 py-2.5">
          <h3 className="font-bold text-sm tracking-tight">
            <span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block 0</span>
            Range Transition — How to read the table
          </h3>
        </header>
        <div className="bg-indigo-50 p-4 text-sm text-indigo-800" data-testid="transitions-info">
          <ul className="list-disc list-inside text-xs space-y-0.5 text-indigo-700">
            <li><strong>Overall</strong>: % across ALL historical draws</li>
            <li><strong>Since 2024</strong>: % from 1st Jan 2024 onwards (recent trend)</li>
            <li><strong>Since 2026</strong>: % across 2026 draws only (current-year trend)</li>
            <li><strong>Same Date</strong>: % specifically on {data.month_name} {data.day} draws</li>
            <li><strong>Diff</strong>: Same Date % minus Since 2024 % (negative = gap to fill, higher chance next)</li>
            <li><strong>Transitions</strong>: Given the previous same-date draw's range, what % came next</li>
            <li><strong>Projection</strong>: Based on the LAST draw's actual range, what's most likely next</li>
            <li className="text-indigo-500">Minimum 4 draws considered for reliable transition data</li>
          </ul>
        </div>
      </section>

      {/* Per-ball transition cards as numbered Blocks (Block 1 → Block 5) */}
      {positions.map((posKey, idx) => {
        const t = rt[posKey];
        if (!t) return null;
        const ranges = t.ranges;
        const projMatch = t.projection;
        const blockNum = idx + 1;
        return (
          <section key={posKey} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`rt-block-${blockNum}`}>
            <header className="bg-slate-800 text-white px-4 py-2.5">
              <h3 className="font-bold text-sm tracking-tight">
                <span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block {blockNum}</span>
                {posLabels[posKey]} — Range Transition
              </h3>
              <p className="text-[11px] text-white/70 mt-0.5">
                Last value: {t.last_value} ({t.last_range}) · Projected next: {projMatch.range} ({projMatch.pct}%)
                {t.cy_draw_count !== undefined && ` · 2026 draws: ${t.cy_draw_count}`}
                {t.recent_draw_count !== undefined && ` · Since-2024 draws: ${t.recent_draw_count}`}
              </p>
            </header>

          <div data-testid={`trans-${posKey}`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm">{posLabels[posKey].replace("Ball ", "B")}</span>
                <span className="font-bold">{posLabels[posKey]}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right text-xs">
                  <div className="text-blue-200">Last: <span className="font-bold text-white">{t.last_value}</span> ({t.last_range})</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-1.5 text-center">
                  <div className="text-[10px] text-blue-200 uppercase">Projection</div>
                  <div className="font-black text-lg">{projMatch.range}</div>
                  <div className="text-[10px] text-blue-200">{projMatch.pct}%</div>
                </div>
              </div>
            </div>

            <div className="p-4">
              {/* Overall vs Same-Date comparison table */}
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                    <th className="text-left py-2 px-2">Range</th>
                    <th className="text-center py-2 px-2">Overall %</th>
                    <th className="text-center py-2 px-2">Since 2024 %</th>
                    <th className="text-center py-2 px-2">Since 2026 %</th>
                    <th className="text-center py-2 px-2">Same Date %</th>
                    <th className="text-center py-2 px-2">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {ranges.map(range => {
                    const overall = t.overall_pct[range] || 0;
                    const recent = t.recent_pct?.[range] || 0;
                    const cy = t.cy_pct?.[range] || 0;
                    const sameDate = t.same_date_pct[range] || 0;
                    const diff = t.difference[range] || 0;
                    const isProjected = range === projMatch.range;

                    return (
                      <tr key={range} className={`border-b border-gray-100 ${isProjected ? "bg-emerald-50" : ""}`}>
                        <td className="py-2 px-2 font-bold text-gray-800">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${isProjected ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-700"}`}>{range}</span>
                        </td>
                        <td className="py-2 px-2 text-center font-mono font-bold text-gray-800">
                          {overall}%
                        </td>
                        <td className="py-2 px-2 text-center font-mono font-bold text-violet-700">
                          {recent}%
                        </td>
                        <td className="py-2 px-2 text-center font-mono font-bold text-fuchsia-700">
                          {cy}%
                        </td>
                        <td className="py-2 px-2 text-center font-mono font-bold text-indigo-700">
                          {sameDate}%
                        </td>
                        <td className="py-2 px-2 text-center">
                          <span className={`text-xs font-bold ${diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-500" : "text-gray-400"}`}>
                            {diff > 0 ? "+" : ""}{diff}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Year-by-year timeline */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-[10px] font-bold text-gray-500 uppercase mb-2">Timeline by Year</div>
                <div className="flex items-center gap-2 flex-wrap">
                  {t.values_by_year.map((v, i) => {
                    const isLast = i === t.values_by_year.length - 1;
                    const rangeColor = v.range === ranges[0] ? "bg-blue-500" : v.range === ranges[ranges.length - 1] ? "bg-purple-500" : "bg-amber-500";
                    return (
                      <div key={i} className="flex items-center gap-1">
                        <div className={`flex flex-col items-center ${isLast ? "ring-2 ring-emerald-400 rounded-lg p-1" : ""}`}>
                          <span className="text-[9px] text-gray-400">{v.year}</span>
                          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center text-white font-bold text-xs ${rangeColor}`}>{v.value}</span>
                          <span className="text-[8px] text-gray-400 mt-0.5">{v.range}</span>
                        </div>
                        {i < t.values_by_year.length - 1 && (
                          <ArrowRight size={10} className="text-gray-300" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          </section>
        );
      })}

      {/* ====================================================== */}
      {/* Block 6 — Lacking-Fill Backtest Summary (Since 2025)   */}
      {/* ====================================================== */}
      {data.range_fill_analysis && Object.keys(data.range_fill_analysis).length > 0 && (
        <section className="bg-white border-2 border-amber-400 rounded-lg overflow-hidden shadow-md" data-testid="rt-block-6">
          <header className="bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600 text-white px-4 py-3">
            <h3 className="font-bold text-sm tracking-tight">
              <span className="inline-block px-2 py-0.5 bg-yellow-300 text-amber-900 rounded mr-2 text-[11px] font-black">Block 6</span>
              Lacking-Fill Backtest — Since 2025 (min 4 draws of context)
            </h3>
            <p className="text-[11px] text-white/90 mt-0.5">
              Walk-forward analysis: at every draw step, identify the most-lacking range vs full-history baseline, then check whether the next draw filled it. Aggregated by severity & range.
            </p>
          </header>
          <div className="p-4 space-y-4">
            {/* Quick verdict cards — current state per ball */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="lacking-fill-verdicts">
              {positions.map(posKey => {
                const rfa = data.range_fill_analysis[posKey];
                if (!rfa || !rfa.current) return null;
                const cur = rfa.current;
                const sevColor = (cur.matched_fill_pct >= 60) ? 'bg-emerald-100 border-emerald-400 text-emerald-900'
                  : (cur.matched_fill_pct >= 40) ? 'bg-amber-100 border-amber-400 text-amber-900'
                  : 'bg-red-50 border-red-300 text-red-800';
                return (
                  <div key={posKey} className={`border-2 rounded-lg p-2 ${sevColor}`} data-testid={`verdict-${posKey}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{posLabels[posKey]}</span>
                      <span className="text-[10px] font-mono opacity-60">n={cur.matched_events}</span>
                    </div>
                    <div className="font-mono font-black text-sm leading-tight">{cur.most_lacking_range}</div>
                    <div className="text-[10px] mt-0.5 opacity-75">lacking <b>{cur.lacking_pct}%</b></div>
                    <div className="text-xl font-mono font-black mt-1">{cur.matched_fill_pct}%</div>
                    <div className="text-[9px] opacity-60 leading-tight">historical fill rate · {cur.matched_severity}</div>
                  </div>
                );
              })}
            </div>

            {/* Detailed per-ball severity & range tables */}
            {positions.map(posKey => {
              const rfa = data.range_fill_analysis[posKey];
              if (!rfa) return null;
              return (
                <div key={`detail-${posKey}`} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`detail-${posKey}`}>
                  <div className="bg-gray-100 px-3 py-2 flex items-center justify-between">
                    <div className="font-bold text-sm text-gray-900">{posLabels[posKey]}</div>
                    <div className="text-[10px] text-gray-500">
                      <b>{rfa.total_events}</b> data points across <b>{rfa.draws_window}</b> draws (since 2025)
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-gray-200">
                    {/* By Severity */}
                    <div className="p-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">By Severity of Lacking</h4>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200 text-[9px] uppercase text-gray-400">
                            <th className="text-left py-1.5">Severity</th>
                            <th className="text-center py-1.5">Events</th>
                            <th className="text-center py-1.5">Filled</th>
                            <th className="text-center py-1.5">Fill %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rfa.by_severity.map(s => {
                            const isCurMatch = rfa.current && rfa.current.matched_severity === s.severity;
                            return (
                              <tr key={s.severity} className={`border-b border-gray-100 ${isCurMatch ? 'bg-amber-50 font-semibold' : ''} ${s.events === 0 ? 'opacity-40' : ''}`}>
                                <td className="py-1 pr-2">{s.severity}</td>
                                <td className="text-center py-1 font-mono">{s.events}</td>
                                <td className="text-center py-1 font-mono">{s.filled}</td>
                                <td className={`text-center py-1 font-mono font-bold ${s.fill_pct >= 60 ? 'text-emerald-700' : s.fill_pct >= 40 ? 'text-amber-700' : s.events > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {s.events > 0 ? `${s.fill_pct}%` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* By Range */}
                    <div className="p-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">By Range (when this range was most-lacking)</h4>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200 text-[9px] uppercase text-gray-400">
                            <th className="text-left py-1.5">Range</th>
                            <th className="text-center py-1.5">Events</th>
                            <th className="text-center py-1.5">Avg Lack</th>
                            <th className="text-center py-1.5">Fill %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rfa.by_range.map(r => {
                            const isCurRange = rfa.current && rfa.current.most_lacking_range === r.range;
                            return (
                              <tr key={r.range} className={`border-b border-gray-100 ${isCurRange ? 'bg-amber-50 font-semibold' : ''} ${r.events === 0 ? 'opacity-40' : ''}`}>
                                <td className="py-1 pr-2"><span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px] font-mono">{r.range}</span></td>
                                <td className="text-center py-1 font-mono">{r.events}</td>
                                <td className="text-center py-1 font-mono text-red-600">{r.avg_lacking_pct}%</td>
                                <td className={`text-center py-1 font-mono font-bold ${r.fill_pct >= 60 ? 'text-emerald-700' : r.fill_pct >= 40 ? 'text-amber-700' : r.events > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                  {r.events > 0 ? `${r.fill_pct}%` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="text-[10px] text-gray-500 italic px-1">
              How to read: <b>Severity</b> = how far a range had drifted below baseline at the moment of analysis. <b>Fill %</b> = what % of those moments saw the next draw land in the lacking range. Higher % = a stronger "mean-reversion" historical pattern. The amber-highlighted row matches the <i>current</i> lacking state of each ball.
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ========== DIGIT GROUPS TAB ========== */
function DigitGroupsTab({ digit }) {
  if (!digit) return <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">No digit data</div>;
  const maxHit = Math.max(...(digit.digit_groups || []).map(dg => dg.hit_count), 1);
  const comp = digit.digit_group_comparison;
  const proj = digit.dg_projection;

  return (
    <div className="space-y-5">
      {/* Block 1 — Recent 5 + Same-Date History side-by-side */}
      {digit.recent5_draws && digit.recent5_draws.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dg-recent5-context">
          {/* Recent 5 (Short-Term) */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-slate-800 text-white">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 bg-indigo-500 rounded text-[11px] font-black">Block 1A</span>
                Recent 5 Draws — Short-Term
              </h3>
              <p className="text-[10px] text-white/70 mt-0.5">Last 5 draws of 2026 (used as "lacking" baseline)</p>
            </div>
            <div className="p-4 space-y-2">
              {digit.recent5_draws.map((rd, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="font-mono text-gray-400 w-20">{rd.date}</span>
                  <div className="flex gap-1 flex-1">
                    {rd.balls.map((b, bi) => (
                      <span key={bi} className="inline-flex flex-col items-center">
                        <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-white font-mono font-bold text-xs ${dgColors[b % 10]}`}>{b}</span>
                        <span className="text-[8px] text-gray-400">d{b % 10}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              <div className="text-[10px] text-gray-500 pt-2 border-t border-gray-100">
                Previous draw: {(digit.prev_draw_numbers || []).map(n => `#${n}`).join(' · ')} · 1-2 typically carry over.
              </div>
            </div>
          </div>

          {/* Draw History on Same Date */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-slate-800 text-white">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 bg-indigo-500 rounded text-[11px] font-black">Block 1B</span>
                Draw History on {digit.month_name} {digit.day}
              </h3>
              <p className="text-[10px] text-white/70 mt-0.5">{digit.total_matched} historical draws on this exact calendar date</p>
            </div>
            <div className="p-4 space-y-2 max-h-[260px] overflow-y-auto" data-testid="dg-same-date-history">
              {(digit.draw_details || []).map((dd, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className="font-mono text-gray-400 w-20">{dd.draw_date}</span>
                  <div className="flex gap-1 flex-1">
                    {dd.balls.map((b, bi) => (
                      <span key={bi} className="inline-flex flex-col items-center">
                        <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-white font-mono font-bold text-xs ${dgColors[b % 10]}`}>{b}</span>
                        <span className="text-[8px] text-gray-400">d{b % 10}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {(!digit.draw_details || digit.draw_details.length === 0) && (
                <div className="text-xs text-gray-400 italic text-center py-4">No prior same-date history.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table: Same Date vs Since 2024 vs Recent 5 */}
      {comp && comp.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="dg-comparison">
          <div className="px-4 py-3 border-b border-gray-100 bg-slate-800 text-white">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-indigo-500 rounded text-[11px] font-black">Block 2</span>
              Digit Group Comparison — Same Date vs Recent 5 vs Since 2024
            </h3>
            <p className="text-[10px] text-white/70 mt-0.5">
              <b>Gap (5)</b> = Same-Date − Recent 5 (positive = lacking in recent draws despite same-date affinity). <b>Diff</b> = Same-Date − Since-2024.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200 bg-gray-50">
                  <th className="text-left py-2 px-3">Digit</th>
                  <th className="text-center py-2 px-2">Same Date %</th>
                  <th className="text-center py-2 px-2">Recent 5 %</th>
                  <th className="text-center py-2 px-2">Gap (5)</th>
                  <th className="text-center py-2 px-2">Since 2024 %</th>
                  <th className="text-center py-2 px-2">Diff</th>
                  <th className="text-center py-2 px-2">Reappear %</th>
                </tr>
              </thead>
              <tbody>
                {comp.map(dg => {
                  const isRecentLacking = dg.is_recent_lacking;
                  const isMissing = dg.same_date_count === 0;
                  const isOver = dg.is_over;
                  return (
                    <tr key={dg.digit} className={`border-b border-gray-100 ${isMissing ? "bg-red-50" : isRecentLacking ? "bg-amber-50" : isOver ? "bg-emerald-50" : ""}`} data-testid={`dg-comp-${dg.digit}`}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-white font-bold text-xs ${dgColors[dg.digit]}`}>{dg.digit}</span>
                          {isMissing && <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">MISSING</span>}
                          {isRecentLacking && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">RECENT-GAP</span>}
                          {isOver && <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">OVER</span>}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-center font-mono font-bold text-indigo-700">{dg.same_date_pct}%</td>
                      <td className="py-2 px-2 text-center font-mono font-bold text-fuchsia-700">{dg.recent5_pct}%</td>
                      <td className="py-2 px-2 text-center">
                        <span className={`text-xs font-bold ${dg.gap_recent5 > 4 ? "text-amber-700" : dg.gap_recent5 < -4 ? "text-red-600" : "text-gray-500"}`}>
                          {dg.gap_recent5 > 0 ? "+" : ""}{dg.gap_recent5}%
                        </span>
                      </td>
                      <td className="py-2 px-2 text-center font-mono font-bold text-violet-700">{dg.since_2024_pct}%</td>
                      <td className="py-2 px-2 text-center">
                        <span className={`text-xs font-bold ${dg.diff > 0 ? "text-emerald-600" : dg.diff < 0 ? "text-red-500" : "text-gray-400"}`}>
                          {dg.diff > 0 ? "+" : ""}{dg.diff}%
                        </span>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className={`text-xs font-bold ${dg.reappearance_pct >= 50 ? "text-emerald-600" : dg.reappearance_pct >= 30 ? "text-amber-600" : "text-gray-400"}`}>
                          {dg.same_date_count > 0 ? `${dg.reappearance_pct}%` : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Block 2.5 — Last-50 Backtest validation */}
      {digit.backtest_50 && digit.backtest_50.events > 0 && (
        <div className="bg-white border-2 border-amber-400 rounded-lg overflow-hidden" data-testid="dg-backtest-50">
          <div className="px-4 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-yellow-300 text-amber-900 rounded text-[11px] font-black">Block 2.5</span>
              Backtest — Validation Across Last 50 Draws
            </h3>
            <p className="text-[10px] text-white/85 mt-0.5">
              Walk-forward simulation: at each of the last {digit.backtest_50.events} draws, what would the projection have been? Did the actual draw align?
            </p>
          </div>

          {/* Currently projected digit groups */}
          {digit.current_top3_dgs && digit.current_top3_dgs.length > 0 && (
            <div className="bg-emerald-50 border-b-2 border-emerald-200 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-2">Currently Projected — Top-3 Lacking Digit Groups</div>
              <div className="flex items-center gap-3 flex-wrap">
                {digit.current_top3_dgs.map((dg, i) => (
                  <div key={dg} className="flex items-center gap-2" data-testid={`current-dg-${i}`}>
                    <span className="text-[10px] text-gray-500 font-mono">#{i + 1}</span>
                    <span className={`inline-flex w-12 h-12 rounded-full items-center justify-center text-white font-mono font-black text-2xl ring-4 ring-emerald-300 ${dgColors[dg]}`}>{dg}</span>
                  </div>
                ))}
                <span className="text-xs text-gray-700 ml-3">
                  → 88% historical hit-rate when ALL three are projected together
                </span>
              </div>
            </div>
          )}

          {/* Per-DG breakdown — what the 88% breaks down to */}
          {digit.backtest_50.per_dg_table && (
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">When Each Digit Group Was Projected (last 50 draws)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase text-gray-400 border-b border-gray-200">
                      <th className="text-left py-1 px-2">Digit</th>
                      <th className="text-center py-1 px-2"># Times Projected</th>
                      <th className="text-center py-1 px-2">Draws With ≥1 Hit</th>
                      <th className="text-center py-1 px-2">Hit Rate</th>
                      <th className="text-center py-1 px-2">Avg Balls Hit / Draw</th>
                    </tr>
                  </thead>
                  <tbody>
                    {digit.backtest_50.per_dg_table.filter(r => r.projected_count > 0).sort((a, b) => b.projected_count - a.projected_count).map(r => {
                      const isCurrent = (digit.current_top3_dgs || []).includes(r.digit);
                      return (
                        <tr key={r.digit} className={`border-b border-gray-100 ${isCurrent ? 'bg-emerald-50' : ''}`} data-testid={`dg-bt-${r.digit}`}>
                          <td className="py-1.5 px-2">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-white font-bold text-[10px] ${dgColors[r.digit]}`}>{r.digit}</span>
                              {isCurrent && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">CURRENT</span>}
                            </div>
                          </td>
                          <td className="text-center py-1 px-2 font-mono">{r.projected_count}×</td>
                          <td className="text-center py-1 px-2 font-mono">{r.any_hit_count}</td>
                          <td className={`text-center py-1 px-2 font-mono font-bold ${r.any_hit_pct >= 50 ? 'text-emerald-700' : r.any_hit_pct >= 30 ? 'text-amber-700' : 'text-red-600'}`}>
                            {r.any_hit_pct}%
                          </td>
                          <td className="text-center py-1 px-2 font-mono text-gray-600">{r.avg_balls_hit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-gray-500 mt-2 italic">
                A digit's hit rate is lower than the combined 88% because the top-3 includes multiple digits — any of them landing in the actual draw counts. Combined, the chance of ≥1 of 3 digits hitting is ~88%.
              </div>
            </div>
          )}

          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* NEW: Pool-source breakdown — the question of "in BOTH blocks vs NEITHER" */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Where Did Next-Draw Numbers Come From? (Pool)</div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-44 text-emerald-800 font-bold">In BOTH Block 1A + 1B</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-emerald-500 h-3" style={{ width: `${digit.backtest_50.pool_in_both_pct}%` }}></div>
                  </div>
                  <span className="font-mono font-bold text-emerald-700 w-12 text-right">{digit.backtest_50.pool_in_both_pct}%</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-44 text-blue-700">In Recent-5 only (Block 1A)</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-blue-500 h-3" style={{ width: `${digit.backtest_50.pool_in_r5_only_pct}%` }}></div>
                  </div>
                  <span className="font-mono font-bold text-blue-700 w-12 text-right">{digit.backtest_50.pool_in_r5_only_pct}%</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-44 text-violet-700">In Same-Date only (Block 1B)</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-violet-500 h-3" style={{ width: `${digit.backtest_50.pool_in_sd_only_pct}%` }}></div>
                  </div>
                  <span className="font-mono font-bold text-violet-700 w-12 text-right">{digit.backtest_50.pool_in_sd_only_pct}%</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-44 text-rose-700 font-bold">In NEITHER (fresh)</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-rose-500 h-3" style={{ width: `${digit.backtest_50.pool_in_neither_pct}%` }}></div>
                  </div>
                  <span className="font-mono font-bold text-rose-700 w-12 text-right">{digit.backtest_50.pool_in_neither_pct}%</span>
                </div>
              </div>
              <div className="text-[10px] text-gray-500 mt-2 italic">
                {digit.backtest_50.pool_in_neither_pct >= 30 ? (
                  <span className="text-rose-700 font-bold">⚠ Surprise: ~{digit.backtest_50.pool_in_neither_pct}% of actual next-draw numbers are FRESH (not in either block). The Verdict block heavily over-weights carry-over — fresh numbers deserve consideration too.</span>
                ) : (
                  <span>Most next-draw numbers come from the Recent-5 + Same-Date pool — the Verdict's bias is justified.</span>
                )}
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                Sample: {digit.backtest_50.actual_numbers_total} actual ball appearances across {digit.backtest_50.events} draws
              </div>
            </div>

            {/* Same-date appearance count split */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Same-Date Appearance Count (Block 1B)</div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-32 text-gray-600">Never on same date</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-blue-500 h-3" style={{ width: `${digit.backtest_50.appeared_zero_pct}%` }}></div>
                  </div>
                  <span className="font-mono font-bold text-blue-700 w-12 text-right">{digit.backtest_50.appeared_zero_pct}%</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-32 text-amber-700 font-bold">✨ Appeared 1× on date</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-amber-500 h-3" style={{ width: `${digit.backtest_50.appeared_once_pct}%` }}></div>
                  </div>
                  <span className="font-mono font-bold text-amber-700 w-12 text-right">{digit.backtest_50.appeared_once_pct}%</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-32 text-gray-600">Appeared 2+× on date</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-purple-500 h-3" style={{ width: `${digit.backtest_50.appeared_multi_pct}%` }}></div>
                  </div>
                  <span className="font-mono font-bold text-purple-700 w-12 text-right">{digit.backtest_50.appeared_multi_pct}%</span>
                </div>
              </div>
              <div className="text-[10px] text-gray-500 mt-2 italic">
                {digit.backtest_50.appeared_once_pct >= digit.backtest_50.appeared_multi_pct * 2 ? (
                  <span className="text-amber-700 font-bold">✓ Confirmed: numbers that appeared once on the same date are ~3× more common than 2+× repeat numbers.</span>
                ) : (
                  <span>Note: appeared-once is moderately useful as a tie-breaker.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block 4 — Next Draw Digit Group Projection (Digit Verdict) */}
      {proj && proj.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg" data-testid="dg-projection">
          <div className="px-4 py-3 border-b border-gray-100 bg-slate-800 text-white">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-indigo-500 rounded text-[11px] font-black">Block 4</span>
              Next Draw Digit Group Projection — Digit Verdict
            </h3>
            <p className="text-[10px] text-white/70">Sorted by headline hit rate (highest first). Top-3 = digit verdict for the next draw.</p>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            {proj.map((p, i) => (
              <div key={p.digit} className={`rounded-lg p-3 border ${i < 3 ? "border-emerald-300 bg-emerald-50" : p.score <= 0 ? "border-gray-200 bg-gray-50 opacity-60" : "border-gray-200 bg-gray-50"}`} data-testid={`proj-dg-${p.digit}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center text-white font-bold text-sm ${i < 3 ? "ring-2 ring-emerald-400" : ""} ${dgColors[p.digit]}`}>{p.digit}</span>
                  <div>
                    <div className={`text-sm font-black ${i < 3 ? "text-emerald-700" : "text-gray-700"}`}>{p.chance_pct}%</div>
                    <div className="text-[9px] text-gray-400">score: {p.score}</div>
                  </div>
                  {i < 3 && <span className="ml-auto text-[9px] font-black text-white bg-emerald-600 px-1.5 py-0.5 rounded">#{i + 1}</span>}
                </div>
                <div className="space-y-0.5">
                  <div className="flex justify-between text-[10px]"><span className="text-gray-500">Same Date</span><span className="font-bold text-indigo-700">{p.same_date_pct}%</span></div>
                  <div className="flex justify-between text-[10px]"><span className="text-gray-500">Since 2024</span><span className="font-bold text-violet-700">{p.since_2024_pct}%</span></div>
                  <div className="flex justify-between text-[10px]"><span className="text-gray-500">Diff</span><span className={`font-bold ${p.diff < 0 ? "text-red-500" : "text-emerald-600"}`}>{p.diff > 0 ? "+" : ""}{p.diff}%</span></div>
                  {p.is_missing_on_date ? (
                    <div className="flex justify-between text-[10px]"><span className="text-gray-500">Fill rate</span><span className="font-bold text-amber-600">{p.fill_when_missing_pct}%</span></div>
                  ) : (
                    <div className="flex justify-between text-[10px]"><span className="text-gray-500">Reappear</span><span className="font-bold text-emerald-600">{p.reappearance_pct}%</span></div>
                  )}
                </div>
                {p.reasons.length > 0 && (
                  <div className="mt-1.5 pt-1.5 border-t border-gray-200">
                    {p.reasons.map((r, j) => (
                      <div key={j} className="text-[8px] text-gray-500">{r}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Digit Verdict summary chips */}
          <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Digit Verdict →</span>
              {proj.slice(0, 3).map((p, i) => (
                <span key={p.digit} className="flex items-center gap-1.5 bg-white rounded-full px-2 py-1 border border-emerald-300" data-testid={`verdict-digit-${i}`}>
                  <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-white font-mono font-black text-xs ${dgColors[p.digit]}`}>{p.digit}</span>
                  <span className="text-[10px] font-bold text-emerald-700">{p.chance_pct}%</span>
                </span>
              ))}
              <span className="text-[10px] text-gray-500 ml-2">→ feeds Block 5 / 6 / 7 / 9 below</span>
            </div>
          </div>
        </div>
      )}

      {/* Block 5 — Did Actual Next-Draw Numbers Come From Projected Digit Groups? */}
      {digit.backtest_50 && digit.backtest_50.events > 0 && (
        <div className="bg-white border-2 border-emerald-300 rounded-lg overflow-hidden" data-testid="dg-projection-pool-match">
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-yellow-300 text-emerald-900 rounded text-[11px] font-black">Block 5</span>
              Did the Actual Next-Draw Numbers Come From Projected Digit Groups? (per pool)
            </h3>
            <p className="text-[10px] text-white/90 mt-0.5">
              Last-50 backtest: a number "matches" if its last digit is in the top-3 projected lacking digit groups at that draw.
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
              <div className="bg-emerald-50 rounded p-2 border border-emerald-200">
                <div className="text-[10px] text-gray-500 uppercase font-bold">Overall</div>
                <div className="text-2xl font-mono font-black text-emerald-700">{digit.backtest_50.dg_match_overall_pct}%</div>
                <div className="text-[10px] text-gray-500">of {digit.backtest_50.actual_numbers_total} numbers</div>
              </div>
              <div className="bg-violet-50 rounded p-2 border border-violet-200">
                <div className="text-[10px] text-violet-700 uppercase font-bold">In Same-Date only</div>
                <div className="text-2xl font-mono font-black text-violet-700">{digit.backtest_50.dg_match_in_sd_only_pct}%</div>
                <div className="text-[10px] text-gray-500">{digit.backtest_50.dg_match_in_sd_only}/{digit.backtest_50.pool_in_sd_only} matched</div>
              </div>
              <div className="bg-rose-50 rounded p-2 border-2 border-rose-300">
                <div className="text-[10px] text-rose-700 uppercase font-bold">In NEITHER (fresh)</div>
                <div className="text-2xl font-mono font-black text-rose-700">{digit.backtest_50.dg_match_in_neither_pct}%</div>
                <div className="text-[10px] text-gray-500">{digit.backtest_50.dg_match_in_neither}/{digit.backtest_50.pool_in_neither} matched</div>
              </div>
              <div className="bg-emerald-50 rounded p-2 border border-emerald-200">
                <div className="text-[10px] text-emerald-700 uppercase font-bold">In BOTH 1A+1B</div>
                <div className="text-2xl font-mono font-black text-emerald-700">{digit.backtest_50.dg_match_in_both_pct}%</div>
                <div className="text-[10px] text-gray-500">{digit.backtest_50.dg_match_in_both}/{digit.backtest_50.pool_in_both} matched</div>
              </div>
              <div className="bg-blue-50 rounded p-2 border border-blue-200">
                <div className="text-[10px] text-blue-700 uppercase font-bold">In Recent-5 only</div>
                <div className="text-2xl font-mono font-black text-blue-700">{digit.backtest_50.dg_match_in_r5_only_pct}%</div>
                <div className="text-[10px] text-gray-500">{digit.backtest_50.dg_match_in_r5_only}/{digit.backtest_50.pool_in_r5_only} matched</div>
              </div>
            </div>
            <div className="text-[10px] text-gray-700 mt-3 leading-relaxed">
              <b className="text-violet-700">Same-Date pool</b> shows the strongest digit-projection signal (~60%) — projecting digits then picking from same-date pool is your best edge.{' '}
              <b className="text-rose-700">Fresh pool</b> (NEITHER) aligns ~31% — digit-projection helpful when filtering fresh candidates.{' '}
              <b className="text-blue-700">Recent-5 only</b> rarely matches (6%) — recent-draw numbers carry digits already heavily represented.
            </div>
          </div>
        </div>
      )}

      {/* Block 6 — Fresh Pool: numbers in NEITHER Block 1A nor 1B */}
      {digit.fresh_pool && digit.fresh_pool.length > 0 && (
        <div className="bg-white border-2 border-rose-400 rounded-lg overflow-hidden" data-testid="dg-fresh-pool">
          <div className="px-4 py-3 bg-gradient-to-r from-rose-600 to-pink-600 text-white">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-yellow-300 text-rose-900 rounded text-[11px] font-black">Block 6</span>
              Fresh Pool — Numbers in NEITHER Block 1A nor 1B
            </h3>
            <p className="text-[10px] text-white/90 mt-0.5">
              {digit.fresh_pool.length} numbers untouched by Recent 5 + Same-Date history. Historical share: ~36% of next-draw numbers come from this pool. Numbers whose last-digit aligns with the top-3 projected lacking digit groups are highlighted as strong fresh candidates.
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-10 gap-2">
              {digit.fresh_pool.map((n) => {
                const isAligned = n.in_top3_dg;
                return (
                  <div key={n.number} className={`rounded-lg p-2 border-2 ${isAligned ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`} data-testid={`fresh-${n.number}`}>
                    <div className={`mx-auto inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-black text-base ${isAligned ? 'bg-emerald-600 text-white ring-2 ring-emerald-300' : 'bg-gray-200 text-gray-800'}`}>
                      {n.number}
                    </div>
                    <div className={`text-[9px] text-center mt-1 font-mono ${isAligned ? 'text-emerald-700 font-bold' : 'text-gray-500'}`}>
                      d{n.digit_group} · {n.dg_chance_pct}%
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-gray-500 mt-3 italic">
              <b className="text-emerald-700">Highlighted (green)</b> = last-digit aligns with one of the top-3 projected lacking digit groups → these are the strongest fresh-pool picks.
            </div>
          </div>
        </div>
      )}

      {/* Block 7 — Top Recommended Numbers — empirically pool-weighted */}
      {digit.top_recommended_numbers && digit.top_recommended_numbers.length > 0 && (
        <div className="bg-white border-2 border-emerald-400 rounded-lg overflow-hidden shadow-md" data-testid="dg-recommended">
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-yellow-300 text-emerald-900 rounded text-[11px] font-black">Block 7</span>
              Top Recommended Numbers — Empirical Pool-Weighted (last-50 backtest)
            </h3>
            <p className="text-[10px] text-white/90 mt-0.5">
              Numbers ranked by pool × digit-alignment empirical match rate. Same-Date pool with digit ∈ top-3 lacking → 60.7% historical hit. Fresh + aligned → 31.1%. Both pools → 28.2%. Recent-5 only → 6.2%.
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-12 gap-2">
              {digit.top_recommended_numbers.map((n, i) => {
                const isElite = i < 3;
                const poolColor =
                  n.pool === "same_date_only"
                    ? "border-violet-500 bg-violet-50"
                    : n.pool === "fresh"
                    ? "border-rose-400 bg-rose-50"
                    : n.pool === "both"
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-blue-300 bg-blue-50";
                const ballBg = isElite
                  ? "bg-emerald-600 text-white ring-2 ring-emerald-300"
                  : n.pool === "same_date_only"
                  ? "bg-violet-500 text-white"
                  : n.pool === "fresh"
                  ? "bg-rose-500 text-white"
                  : n.pool === "both"
                  ? "bg-emerald-500 text-white"
                  : "bg-blue-400 text-white";
                const poolLabel = {
                  same_date_only: "Same-Date",
                  fresh: "Fresh",
                  both: "Both 1A+1B",
                  recent5_only: "Recent-5",
                }[n.pool];
                return (
                  <div key={n.number} className={`rounded-lg p-2 border-2 relative ${poolColor}`} data-testid={`rec-num-${n.number}`}>
                    {n.appeared_once_on_date && (
                      <div className="absolute -top-1 -right-1 bg-amber-500 text-white text-[8px] font-black px-1 py-0.5 rounded-full">1×</div>
                    )}
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[8px] text-gray-400 font-mono">#{i + 1}</span>
                      <span className="text-[8px] text-gray-500">d{n.digit_group}</span>
                    </div>
                    <div className={`mx-auto inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-black text-base ${ballBg}`}>
                      {n.number}
                    </div>
                    <div className="text-[10px] text-center font-mono font-bold mt-1 text-emerald-700">{n.pool_match_rate_pct}%</div>
                    <div className="text-[9px] text-center text-gray-600 mt-0.5 leading-tight">
                      {poolLabel}
                      {n.in_top3_dg ? <span className="block text-emerald-700 font-bold">d✓ top-3</span> : <span className="block text-gray-400">d off-top3</span>}
                      {n.in_prev_draw && <span className="block text-amber-700 font-bold">prev draw</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-gray-500 mt-3 italic">
              Strength = empirical pool match-rate × digit-alignment multiplier (1.0 if digit ∈ top-3 lacking, 0.33 otherwise) + small bonuses for once-only (+0.3) and prev-draw (+0.2). Top-3 are highlighted as elite picks.{' '}
              <span className="ml-2"><span className="inline-block w-2 h-2 rounded bg-violet-500 mr-1"></span>Same-Date</span>{' '}
              <span><span className="inline-block w-2 h-2 rounded bg-emerald-500 mr-1"></span>Both</span>{' '}
              <span><span className="inline-block w-2 h-2 rounded bg-rose-500 mr-1"></span>Fresh</span>{' '}
              <span><span className="inline-block w-2 h-2 rounded bg-blue-400 mr-1"></span>Recent-5</span>
            </div>
          </div>
        </div>
      )}

      {/* Block 8 — Number Reappearance Chance by Frequency */}
      <div className="bg-white border-2 border-indigo-300 rounded-lg overflow-hidden" data-testid="block-8-reappearance-wrapper">
        <div className="px-4 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <span className="inline-block px-2 py-0.5 bg-yellow-300 text-indigo-900 rounded text-[11px] font-black">Block 8</span>
            Number Reappearance Chance on {digit.month_name} {digit.day}
          </h3>
          <p className="text-[10px] text-white/90 mt-0.5">
            Numbers that appeared on this date before, grouped by frequency. Higher frequency = higher chance of appearing in the next draw.
          </p>
        </div>
        <div className="p-0">
          <NumberReappearancePanel digit={digit} />
        </div>
      </div>

      {/* Block 9 — FINAL NUMBER VERDICT */}
      <FinalNumberVerdict digit={digit} proj={proj} />


      {/* Missing Digit Groups — gap fill context */}
      {digit.missing_groups && digit.missing_groups.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-red-50 rounded-lg border border-amber-200 p-4" data-testid="missing-groups">
          <h3 className="text-sm font-bold text-amber-800 mb-1">Missing Digit Groups on This Date</h3>
          <p className="text-[10px] text-amber-700 mb-3">Never appeared on {digit.month_name} {digit.day}. Underrepresented vs Since 2024 = gap to fill = higher chance next.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {digit.missing_groups.map(dg => {
              const compData = comp?.find(c => c.digit === dg.digit);
              return (
                <div key={dg.digit} className="bg-white rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center text-white font-bold text-sm ${dgColors[dg.digit]}`}>{dg.digit}</span>
                    <div>
                      <span className="text-xs font-bold text-amber-800">Group {dg.digit}</span>
                      {compData && <div className="text-[10px] text-gray-500">Gap: <strong className="text-red-600">{compData.diff}%</strong> | Fill rate: <strong className="text-amber-700">{compData.fill_when_missing_pct}%</strong> ({compData.missing_filled}/{compData.missing_dates})</div>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {dg.all_numbers.map(n => (
                      <span key={n} className="inline-flex w-7 h-7 rounded-full items-center justify-center bg-amber-100 text-amber-800 font-bold text-[10px] border border-amber-300">{n}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fill Validation */}
      {digit.fill_validation && (
        <div className="bg-white border border-gray-200 rounded-lg" data-testid="fill-validation">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-700">Fill Validation — Do draws fill missing digit groups?</h3>
          </div>
          <div className="p-4">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200 mb-4">
              <div className="flex items-center gap-6 flex-wrap">
                <div><div className="text-2xl font-black text-emerald-700">{digit.fill_validation.fill_rate_pct}%</div><div className="text-[10px] text-emerald-600 font-medium">Fill Rate</div></div>
                <div className="text-xs text-gray-600"><strong>{digit.fill_validation.total_dates_filled}</strong> out of <strong>{digit.fill_validation.total_dates_with_missing}</strong> dates with missing groups had at least 1 gap filled.</div>
              </div>
            </div>
            {digit.missing_groups && digit.missing_groups.length > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-3 border border-amber-200">
                <div className="text-xs font-bold text-amber-800 mb-1">Prediction Signal</div>
                <div className="text-[11px] text-amber-700">
                  Groups {digit.missing_groups.map(mg => mg.digit).join(" & ")} never appeared on this date. With a <strong>{digit.fill_validation.fill_rate_pct}%</strong> fill rate, strong signal for:
                  <span className="font-bold ml-1">{digit.missing_groups.map(mg => `Group ${mg.digit} (${mg.all_numbers.join(", ")})`).join(" | ")}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Number Recurrence */}
      {digit.number_recurrence && (
        <div className="bg-white border border-gray-200 rounded-lg" data-testid="number-recurrence">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-700">Most Frequent Number Recurrence</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-3 border border-violet-200">
                <div className="text-2xl font-black text-violet-700">{digit.number_recurrence.exact_rate_pct}%</div>
                <div className="text-[10px] text-violet-600 font-medium">Exact Number Hit</div>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-3 border border-indigo-200">
                <div className="text-2xl font-black text-indigo-700">{digit.number_recurrence.dg_rate_pct}%</div>
                <div className="text-[10px] text-indigo-600 font-medium">Digit Group Hit</div>
              </div>
            </div>
            {digit.number_recurrence.target_top_numbers?.length > 0 && (
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-3 border border-violet-200">
                <div className="text-xs font-bold text-violet-800 mb-2">Top Numbers on {digit.month_name} {digit.day}</div>
                <div className="flex flex-wrap gap-2">
                  {digit.number_recurrence.target_top_numbers.map((tn, i) => (
                    <div key={i} className="flex items-center gap-1 bg-white rounded-lg px-2 py-1 border border-violet-200">
                      <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-white font-bold text-xs ${dgColors[tn.digit_group]}`}>{tn.number}</span>
                      <span className="text-[10px] font-bold text-violet-700">{tn.count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== OVERLAP TAB ========== */

/* ========== NUMBER REAPPEARANCE PANEL ========== */
function NumberReappearancePanel({ digit }) {
  if (!digit?.number_overlap) return null;
  const pool = digit.number_overlap.target_pool_detail || [];
  const freqInsight = digit.number_overlap.deep_dive?.freq_insight || [];

  // Build frequency rate map: {1: 13.7, 2: 18.6, 3: 23.4, 4: 15.1}
  const freqRates = {};
  for (const fi of freqInsight) {
    freqRates[fi.freq] = fi.hit_rate_pct;
  }

  // Group numbers by frequency
  const byFreq = {};
  for (const n of pool) {
    const f = n.freq;
    if (!byFreq[f]) byFreq[f] = [];
    byFreq[f].push(n);
  }

  // Sort frequency buckets descending
  const freqBuckets = Object.keys(byFreq).map(Number).sort((a, b) => b - a);
  if (freqBuckets.length === 0) return null;

  const getBucketRate = (f) => {
    if (f >= 4) return freqRates[4] || 0;
    return freqRates[f] || 0;
  };

  const getBucketColor = (f) => {
    if (f >= 3) return { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", ball: "bg-emerald-600", badge: "bg-emerald-100 text-emerald-800" };
    if (f === 2) return { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-700", ball: "bg-blue-600", badge: "bg-blue-100 text-blue-800" };
    return { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600", ball: "bg-gray-500", badge: "bg-gray-100 text-gray-700" };
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg" data-testid="number-reappearance">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-bold text-gray-700">Number Reappearance Chance on {digit.month_name} {digit.day}</h3>
        <p className="text-[10px] text-gray-500">Numbers that appeared on this date before, grouped by frequency. Higher frequency = higher chance of appearing in the next draw.</p>
      </div>
      <div className="p-4 space-y-4">
        {/* Frequency rate legend */}
        <div className="flex flex-wrap gap-3 bg-indigo-50 rounded-lg p-3 border border-indigo-200">
          <div className="text-xs font-bold text-indigo-800 mr-2">Reappearance Rule:</div>
          {freqInsight.map(fi => (
            <div key={fi.freq} className="flex items-center gap-1.5">
              <span className={`text-xs font-black ${fi.hit_rate_pct >= 20 ? "text-emerald-600" : fi.hit_rate_pct >= 15 ? "text-blue-600" : "text-gray-600"}`}>{fi.hit_rate_pct}%</span>
              <span className="text-[10px] text-indigo-700">for {fi.label} numbers</span>
            </div>
          ))}
        </div>

        {/* Numbers grouped by frequency */}
        {freqBuckets.map(freq => {
          const numbers = byFreq[freq];
          const rate = getBucketRate(freq);
          const colors = getBucketColor(freq);
          const label = freq >= 4 ? `${freq}+x` : `${freq}x`;

          return (
            <div key={freq} className={`rounded-lg p-4 border ${colors.bg} ${colors.border}`} data-testid={`freq-bucket-${freq}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-black ${colors.text}`}>Appeared {label}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${colors.badge}`}>{numbers.length} numbers</span>
                </div>
                <div className="text-right">
                  <span className={`text-xl font-black ${rate >= 20 ? "text-emerald-600" : rate >= 15 ? "text-blue-600" : "text-gray-600"}`}>{rate}%</span>
                  <div className="text-[9px] text-gray-400">chance each</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {numbers.sort((a, b) => a.number - b.number).map(n => (
                  <div key={n.number} className="flex flex-col items-center">
                    <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center text-white font-bold text-sm ${freq >= 3 ? "ring-2 ring-emerald-400" : ""} ${dgColors[n.digit_group]}`}>{n.number}</span>
                    <span className="text-[8px] text-gray-400 mt-0.5">DG{n.digit_group}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Numbers NOT in pool (never appeared on this date) */}
        {(() => {
          const poolNums = new Set(pool.map(n => n.number));
          const notInPool = [];
          for (let i = 1; i <= 50; i++) {
            if (!poolNums.has(i)) notInPool.push(i);
          }
          if (notInPool.length === 0) return null;
          return (
            <div className="rounded-lg p-4 border border-red-200 bg-red-50" data-testid="never-on-date">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-black text-red-700">Never on this date</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-700">{notInPool.length} numbers</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-red-600">Lower individual chance — but may fill digit group gaps</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {notInPool.map(n => (
                  <span key={n} className="inline-flex w-7 h-7 rounded-full items-center justify-center bg-red-100 text-red-600 font-bold text-[10px] border border-red-200">{n}</span>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/* ========== FINAL NUMBER VERDICT (Block 9) ========== */
function FinalNumberVerdict({ digit, proj }) {
  if (!digit?.top_recommended_numbers || digit.top_recommended_numbers.length === 0) return null;

  // Build reappearance frequency map: {number: freq_on_this_date}
  const freqMap = {};
  const pool = digit?.number_overlap?.target_pool_detail || [];
  for (const p of pool) freqMap[p.number] = p.freq;

  // Build top-3 digit set
  const top3Digits = (proj || []).slice(0, 3).map(p => p.digit);

  // Re-rank top_recommended with reappearance boost
  const enriched = digit.top_recommended_numbers.map(n => {
    const rfreq = freqMap[n.number] || 0;
    // Reappearance boost — empirical from Block 8 freq insight
    let reappBoost = 0;
    if (rfreq >= 3) reappBoost = 0.6;
    else if (rfreq === 2) reappBoost = 0.3;
    else if (rfreq === 1) reappBoost = 0.1;
    const finalScore = (n.strength || 0) + reappBoost;
    return { ...n, reappearance_freq: rfreq, final_score: Math.round(finalScore * 100) / 100 };
  });
  enriched.sort((a, b) => b.final_score - a.final_score);

  const finalSeven = enriched.slice(0, 7);
  const reserveThree = enriched.slice(7, 10);

  // Compute composite confidence (avg pool match × digit alignment)
  const avgConfidence = finalSeven.length > 0
    ? Math.round(finalSeven.reduce((s, n) => s + (n.pool_match_rate_pct || 0), 0) / finalSeven.length)
    : 0;

  const tierColor = (i) => {
    if (i < 3) return { ring: "ring-4 ring-amber-300", bg: "bg-gradient-to-br from-amber-500 to-orange-600", label: "ELITE" };
    if (i < 5) return { ring: "ring-2 ring-emerald-300", bg: "bg-gradient-to-br from-emerald-500 to-teal-600", label: "STRONG" };
    return { ring: "ring-2 ring-blue-300", bg: "bg-gradient-to-br from-blue-500 to-indigo-600", label: "SUPPORT" };
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 border-4 border-amber-400 rounded-xl overflow-hidden shadow-2xl" data-testid="block-9-final-verdict">
      <div className="px-5 py-4 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500">
        <div className="flex items-center gap-2">
          <span className="inline-block px-2.5 py-1 bg-slate-900 text-amber-300 rounded text-[11px] font-black tracking-wider">BLOCK 9 · FINAL</span>
          <h3 className="text-base font-black text-white tracking-tight">FINAL NUMBER VERDICT — {digit.month_name} {digit.day}</h3>
        </div>
        <p className="text-[10px] text-white/90 mt-1">
          Composite of: digit-group projection (Block 4) × pool-weighted strength (Block 7) × reappearance frequency (Block 8). The 7 strongest picks for the next draw.
        </p>
      </div>

      {/* Digit Verdict Reminder Strip */}
      <div className="px-5 py-2 bg-slate-800 border-b border-slate-700 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-amber-300 font-bold uppercase tracking-wider">Driving Digits →</span>
        {top3Digits.map((d, i) => (
          <span key={d} className="flex items-center gap-1 bg-slate-700 rounded-full px-2 py-0.5">
            <span className={`inline-flex w-5 h-5 rounded-full items-center justify-center font-mono font-black text-[10px] text-white ${dgColors[d]}`}>{d}</span>
            <span className="text-[9px] font-bold text-amber-200">#{i + 1}</span>
          </span>
        ))}
        <span className="ml-auto text-[10px] text-emerald-300 font-mono">avg pool-match {avgConfidence}%</span>
      </div>

      {/* Final 7 Picks */}
      <div className="p-5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 mb-3">★ Top 7 Picks (Final Lottery Slip)</div>
        <div className="grid grid-cols-7 gap-2">
          {finalSeven.map((n, i) => {
            const t = tierColor(i);
            return (
              <div key={n.number} className="text-center" data-testid={`final-pick-${i}`}>
                <div className={`inline-flex w-14 h-14 rounded-full items-center justify-center font-mono font-black text-xl text-white ${t.bg} ${t.ring} shadow-lg`}>
                  {n.number}
                </div>
                <div className="text-[9px] font-bold text-amber-300 mt-1.5">{t.label}</div>
                <div className="text-[8px] text-slate-300 font-mono mt-0.5">d{n.digit_group}{n.in_top3_dg ? '✓' : ''}</div>
                <div className="text-[8px] text-emerald-300 font-mono">{n.pool_match_rate_pct}%</div>
                {n.reappearance_freq > 0 && (
                  <div className="text-[8px] text-violet-300 font-bold mt-0.5">★{n.reappearance_freq}× date</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Reserve picks */}
        {reserveThree.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-700">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Reserve / Wildcard (next 3)</div>
            <div className="flex gap-2 flex-wrap">
              {reserveThree.map((n) => (
                <div key={n.number} className="flex items-center gap-1.5 bg-slate-800 rounded-lg px-2 py-1.5 border border-slate-700" data-testid={`final-reserve-${n.number}`}>
                  <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-xs text-white ${dgColors[n.digit_group]}`}>{n.number}</span>
                  <span className="text-[9px] text-slate-300">d{n.digit_group}</span>
                  <span className="text-[9px] text-emerald-400 font-mono">{n.pool_match_rate_pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pool composition summary */}
        <div className="mt-5 pt-4 border-t border-slate-700">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Pool Composition (final 7)</div>
          <div className="flex gap-3 flex-wrap text-[10px]">
            {(() => {
              const counts = { same_date_only: 0, fresh: 0, both: 0, recent5_only: 0 };
              for (const n of finalSeven) counts[n.pool] = (counts[n.pool] || 0) + 1;
              const labels = {
                same_date_only: { name: "Same-Date", color: "text-violet-300", dot: "bg-violet-500" },
                fresh: { name: "Fresh", color: "text-rose-300", dot: "bg-rose-500" },
                both: { name: "Both 1A+1B", color: "text-emerald-300", dot: "bg-emerald-500" },
                recent5_only: { name: "Recent-5", color: "text-blue-300", dot: "bg-blue-500" },
              };
              return Object.entries(labels).map(([k, l]) => (
                counts[k] > 0 ? (
                  <div key={k} className="flex items-center gap-1.5">
                    <span className={`inline-block w-2 h-2 rounded-full ${l.dot}`}></span>
                    <span className={l.color}><b>{counts[k]}</b> {l.name}</span>
                  </div>
                ) : null
              ));
            })()}
          </div>
        </div>

        <div className="mt-4 text-[10px] text-slate-400 italic leading-relaxed">
          Final ranking = pool-weighted strength + reappearance boost (3+× = +0.6, 2× = +0.3, 1× = +0.1). <b className="text-amber-300">Elite (top 3)</b> combine the strongest digit alignment with proven date affinity. <b className="text-emerald-300">Strong (4-5)</b> are second-tier. <b className="text-blue-300">Support (6-7)</b> round out coverage. Reserve 3 are wildcards if you play more than 7.
        </div>
      </div>
    </div>
  );
}



function OverlapTab({ digit }) {
  if (!digit?.number_overlap) return <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">No overlap data</div>;
  const no = digit.number_overlap;
  const dd = no.deep_dive;
  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-lg p-3 border border-cyan-200"><div className="text-2xl font-black text-cyan-700">{no.avg_overlap}/7</div><div className="text-[10px] text-cyan-600 font-medium">Avg Overlap</div></div>
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200"><div className="text-2xl font-black text-green-700">{no.full_7_count}</div><div className="text-[10px] text-green-600 font-medium">7/7 Match</div></div>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200"><div className="text-2xl font-black text-blue-700">{no.at_least_5}</div><div className="text-[10px] text-blue-600 font-medium">5+ Overlap</div></div>
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-3 border border-violet-200"><div className="text-2xl font-black text-violet-700">{no.at_least_3}</div><div className="text-[10px] text-violet-600 font-medium">3+ Overlap</div></div>
      </div>

      {/* Distribution */}
      <div className="bg-white border border-gray-200 rounded-lg" data-testid="overlap-dist">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50"><h3 className="text-sm font-bold text-gray-700">Overlap Distribution</h3></div>
        <div className="p-4 space-y-1">
          {Object.entries(no.distribution).map(([k, v]) => {
            const maxV = Math.max(...Object.values(no.distribution), 1);
            const pct = no.total_tested > 0 ? Math.round(v / no.total_tested * 100) : 0;
            return (
              <div key={k} className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-gray-500 w-8 text-right">{k}/7</span>
                <div className="flex-1 bg-gray-100 rounded-full h-4"><div className={`h-4 rounded-full ${parseInt(k) >= 5 ? 'bg-green-500' : parseInt(k) >= 3 ? 'bg-blue-400' : 'bg-gray-300'}`} style={{ width: `${(v / maxV) * 100}%` }} /></div>
                <span className="text-[10px] text-gray-500 w-16">{v} ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Deep Dive Insights */}
      {dd && (
        <div className="space-y-4">
          {/* Frequency insight */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200" data-testid="freq-insight">
            <div className="text-xs font-bold text-amber-800 mb-2">Prior Frequency Predicts Overlap</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {dd.freq_insight?.map((fi, i) => (
                <div key={i} className={`rounded-lg p-2 border text-center ${fi.hit_rate_pct > 20 ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="text-sm font-black text-gray-800">{fi.hit_rate_pct}%</div>
                  <div className="text-[10px] text-gray-600 font-bold">{fi.label} in prior</div>
                </div>
              ))}
            </div>
          </div>

          {/* Position overlap */}
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-4 border border-violet-200" data-testid="pos-insight">
            <div className="text-xs font-bold text-violet-800 mb-2">Position Overlap Rates</div>
            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {dd.position_insight?.map((pi, i) => (
                <div key={i} className={`rounded-lg p-2 border text-center ${pi.overlap_rate_pct > 60 ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="text-xs font-black text-gray-800">{pi.overlap_rate_pct}%</div>
                  <div className="text-[9px] text-gray-500 font-bold">{pi.position.replace('Ball ', 'B').replace('Lucky Star ', 'LS')}</div>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-violet-700">Same position hit: <strong>{dd.same_pos_rate_pct}%</strong></div>
          </div>

          {/* Low vs High */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200" data-testid="lowhigh-insight">
            <div className="text-xs font-bold text-emerald-800 mb-2">Low Numbers (1-25) Dominate Overlaps</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold text-gray-600 mb-1">Overlapping</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{width: `${dd.low_overlap_pct}%`}} /></div>
                  <span className="text-[10px] font-bold text-emerald-700 w-16">Low {dd.low_overlap_pct}%</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-gray-600 mb-1">New Numbers</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden"><div className="h-full bg-red-400 rounded-full" style={{width: `${dd.low_new_pct}%`}} /></div>
                  <span className="text-[10px] font-bold text-red-600 w-16">Low {dd.low_new_pct}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Target Pool */}
      {no.target_pool_detail && no.target_pool_detail.length > 0 && (
        <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-lg p-4 border border-cyan-200" data-testid="target-pool">
          <div className="text-xs font-bold text-cyan-800 mb-1">Prediction Pool — {no.target_date_pool_count} unique numbers from {digit.total_matched} prior draws</div>
          <div className="text-[10px] text-cyan-700 mb-2">Avg overlap: <strong>{no.avg_overlap}/7</strong>. Numbers with <strong>2+ freq</strong> and <strong>low range</strong> have highest overlap probability.</div>
          <div className="flex flex-wrap gap-1.5">
            {no.target_pool_detail.map(n => (
              <div key={n.number} className="flex flex-col items-center">
                <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-bold text-[10px] text-white ${n.freq >= 2 ? 'ring-2 ring-yellow-400' : ''} ${dgColors[n.digit_group]}`}>{n.number}</span>
                <span className={`text-[8px] font-bold mt-0.5 ${n.freq >= 2 ? 'text-amber-700' : 'text-gray-400'}`}>{n.freq}x</span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[9px] text-gray-500">Gold ring = 2+ appearances (higher overlap probability)</div>
        </div>
      )}

      {/* Recent results table */}
      {no.recent_results && no.recent_results.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg" data-testid="overlap-recent">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50"><h3 className="text-sm font-bold text-gray-700">Recent Draws (2024-2026)</h3></div>
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 text-[10px]">
                  <th className="text-left py-1.5 px-2">Date</th>
                  <th className="text-center py-1.5 px-2">Prior</th>
                  <th className="text-center py-1.5 px-2">Overlap</th>
                  <th className="text-left py-1.5 px-2">Draw Numbers</th>
                  <th className="text-left py-1.5 px-2">Matched</th>
                  <th className="text-left py-1.5 px-2">New</th>
                </tr>
              </thead>
              <tbody>
                {no.recent_results.map((r, i) => (
                  <tr key={i} className={`border-b border-gray-50 ${r.overlap_count >= 5 ? 'bg-green-50' : r.overlap_count >= 3 ? 'bg-blue-50' : ''}`}>
                    <td className="py-1.5 px-2 font-bold text-gray-700 whitespace-nowrap">{r.date}</td>
                    <td className="py-1.5 px-2 text-center text-gray-500">{r.prior_count}</td>
                    <td className="py-1.5 px-2 text-center"><span className={`font-bold ${r.overlap_count >= 5 ? 'text-green-600' : r.overlap_count >= 3 ? 'text-blue-600' : 'text-gray-500'}`}>{r.overlap_count}/7</span></td>
                    <td className="py-1.5 px-2"><div className="flex gap-0.5 flex-wrap">{r.current_numbers.map((n, j) => (<span key={j} className={`inline-flex w-5 h-5 rounded-full items-center justify-center font-bold text-[8px] ${r.overlap.includes(n) ? 'bg-green-500 text-white' : 'bg-red-100 text-red-700'}`}>{n}</span>))}</div></td>
                    <td className="py-1.5 px-2"><div className="flex gap-0.5 flex-wrap">{r.overlap.map((n, j) => (<span key={j} className="inline-flex w-5 h-5 rounded-full items-center justify-center bg-green-500 text-white font-bold text-[8px]">{n}</span>))}</div></td>
                    <td className="py-1.5 px-2"><div className="flex gap-0.5 flex-wrap">{r.not_in_prior.map((n, j) => (<span key={j} className="inline-flex w-5 h-5 rounded-full items-center justify-center bg-red-100 text-red-700 font-bold text-[8px]">{n}</span>))}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


    </div>
  );
}


/* ============================================================
   ConsecutiveDrawsTab — formerly Window 31 (Shared number last draw).
   Merged here on 2026-04-30 as part of W57 → W31 unification.
   ============================================================ */
function ConsecutiveDrawsTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sharedSet, setSharedSet] = useState(1);
  const [myShared, setMyShared] = useState('');
  const [sharedApplied, setSharedApplied] = useState(false);
  const [dbSets, setDbSets] = useState({});

  const handleSharedSetChange = (s) => {
    setSharedSet(s);
    const saved = dbSets[`C${s}`]?.shared_prediction || '';
    setMyShared(saved);
    setSharedApplied(!!saved);
  };

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [res, csRes] = await Promise.all([
        axios.get(`${API}/api/analysis/pattern-replication`),
        axios.get(`${API}/api/analysis/constraint-sets`),
      ]);
      setData(res.data);
      const cs = csRes.data || {};
      setDbSets(cs);
      if (!silent) {
        const c1Shared = cs.C1?.shared_prediction || '';
        setMyShared(c1Shared);
        setSharedApplied(!!c1Shared);
      }
    } catch { if (!silent) toast.error('Failed to fetch pattern replication'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(false); }, []);
  useDrawsUpdated(fetchData);

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="consecutive-draws-tab">
      <Toaster position="top-right" richColors />
      <PageConsensusBanner className="mb-4" />
      {data?.risk_flags && Object.keys(data.risk_flags).length > 0 && (
        <BlindSpotsBanner
          riskFlags={data.risk_flags}
          topPicks={Object.keys(data.risk_flags).map(k => ({ id: k, tier: 'WINDOW' }))}
          itemLabel="signals"
          renderItem={() => <span className="font-bold">Shared-Number Signal</span>}
        />
      )}
      <div className="flex justify-end">
        <ExportButton data={data?.last_20} filename="w31_pattern_replication" />
      </div>

      {/* Block 1 — My Replication Prediction */}
      <div className={`rounded-lg p-5 text-white transition-all ${sharedApplied ? 'bg-gradient-to-r from-emerald-600 to-green-700 ring-2 ring-emerald-400' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`} data-testid="my-prediction-shared">
        <div className="flex items-center gap-1.5 mb-3" data-testid="shared-set-tabs">
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => handleSharedSetChange(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${sharedSet === s ? (s===1?'bg-blue-600':s===2?'bg-purple-600':'bg-teal-600')+' text-white ring-2 ring-white/40 shadow-lg' : !!dbSets[`C${s}`]?.shared_prediction ? 'bg-white/20 text-white/90' : 'bg-white/10 text-white/50 hover:bg-white/15'}`} data-testid={`shared-set-${s}`}>C{s}</button>
          ))}
          <span className="text-[10px] text-white/40 ml-2">3 independent constraint sets</span>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-lg">Block 1 — My Replication Prediction <span className="text-sm opacity-70">(C{sharedSet})</span></h3>
            <p className="text-xs text-white/70">How many numbers do you expect to repeat from the previous draw?</p>
          </div>
          {sharedApplied ? (
            <span className="text-xs bg-emerald-400/30 px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 ring-1 ring-emerald-300">
              <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span> APPLIED
            </span>
          ) : (
            <span className="text-xs bg-white/10 px-3 py-1.5 rounded-full font-bold text-white/60">NOT SET</span>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-3 bg-white/10 rounded-lg px-4 py-2.5">
          {[1,2,3,4,5].map(s => {
            const val = dbSets[`C${s}`]?.shared_prediction;
            return (
              <div key={s} className={`flex items-center gap-1.5 text-xs ${sharedSet === s ? 'ring-1 ring-white/40 rounded px-1.5 py-0.5 bg-white/10' : ''}`}>
                <span className={`font-bold ${val ? 'text-emerald-300' : 'text-white/30'}`}>C{s}:</span>
                {val ? (
                  <span className="font-mono font-bold text-white/90">{val.split(',').join(' or ')} shared</span>
                ) : (
                  <span className="text-white/30 italic">not set</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold opacity-80">Expected Shared:</label>
            <select value={myShared} onChange={e => { setMyShared(e.target.value); setSharedApplied(false); }}
              className="w-36 px-2 py-1.5 rounded text-gray-900 font-mono font-bold text-center text-sm" data-testid="shared-select">
              <option value="">-</option>
              <option value="0">0 Shared</option>
              <option value="1">1 Shared</option>
              <option value="2">2 Shared</option>
              <option value="3">3 Shared</option>
              <option value="0,1">0 or 1 Shared</option>
              <option value="0,2">0 or 2 Shared</option>
              <option value="0,3">0 or 3 Shared</option>
              <option value="1,2">1 or 2 Shared</option>
              <option value="2,3">2 or 3 Shared</option>
            </select>
          </div>
          {myShared && !myShared.includes(',') && (data.shared_numbers || []).find(s => s.shared === parseInt(myShared, 10)) && (
            <span className="text-xs bg-white/15 rounded px-2 py-1">
              Historical: {(data.shared_numbers || []).find(s => s.shared === parseInt(myShared, 10))?.pct}% chance
            </span>
          )}
          <Button onClick={() => {
            setSharedApplied(true); setDbSets(prev => ({ ...prev, [`C${sharedSet}`]: { ...prev[`C${sharedSet}`], shared_prediction: myShared } })); syncConstraintsToDB(sharedSet, { shared_prediction: myShared }); toast.success(`Replication C${sharedSet} applied: ${myShared}`); }}
            className={`font-bold transition-all ${sharedApplied ? 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-emerald-300' : 'bg-amber-400 text-amber-900 hover:bg-amber-300'}`} data-testid="save-shared-btn">
            {sharedApplied ? 'Applied' : 'Save Constraint'}
          </Button>
          {sharedApplied && (
            <Button onClick={() => { setSharedApplied(false); setMyShared(''); setDbSets(prev => ({ ...prev, [`C${sharedSet}`]: { ...prev[`C${sharedSet}`], shared_prediction: null } })); syncConstraintsToDB(sharedSet, { shared_prediction: null }); toast.info(`Replication C${sharedSet} cleared`); }}
              variant="ghost" className="text-white/70 hover:text-white text-xs">Clear</Button>
          )}
        </div>
      </div>

      {/* Block 2 — Repeat Rates */}
      <div>
        <h3 className="font-bold text-gray-900 mb-3">Block 2 — Repeat Rates (Consecutive Draws)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border rounded p-4 bg-blue-50 border-blue-200">
            <p className="text-xs uppercase font-bold text-blue-600">Same Sum Range (\u00b110)</p>
            <p className="text-2xl font-bold text-blue-700 mt-1" data-testid="stat-sum">{data.repeat_rates.same_sum_range.pct}%</p>
            <p className="text-xs text-gray-500">{data.repeat_rates.same_sum_range.count} consecutive pairs</p>
          </div>
          <div className="bg-white border rounded p-4 bg-green-50 border-green-200">
            <p className="text-xs uppercase font-bold text-green-600">Same Spread Range (\u00b15)</p>
            <p className="text-2xl font-bold text-green-700 mt-1" data-testid="stat-spread">{data.repeat_rates.same_spread_range.pct}%</p>
            <p className="text-xs text-gray-500">{data.repeat_rates.same_spread_range.count} consecutive pairs</p>
          </div>
          <div className="bg-white border rounded p-4 bg-amber-50 border-amber-200">
            <p className="text-xs uppercase font-bold text-amber-600">Same Odd Count</p>
            <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="stat-odd">{data.repeat_rates.same_odd_count.pct}%</p>
            <p className="text-xs text-gray-500">{data.repeat_rates.same_odd_count.count} consecutive pairs</p>
          </div>
        </div>
      </div>

      {/* Block 3 — Shared Numbers Distribution */}
      <div className="bg-white border rounded p-4">
        <h3 className="font-bold text-gray-900 mb-3">Block 3 — Shared Numbers Between Consecutive Draws</h3>
        <div className="space-y-2" data-testid="shared-dist">
          {(data.shared_numbers || []).map(sn => {
            const max = Math.max(...data.shared_numbers.map(x => x.count));
            return (
              <div key={sn.shared} className="flex items-center gap-2">
                <span className="font-mono text-sm w-20 text-right font-bold">{sn.shared} shared</span>
                <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden relative">
                  <div className={`h-full rounded ${sn.shared === 0 ? 'bg-gray-400' : sn.shared <= 1 ? 'bg-blue-500' : 'bg-[#002FA7]'}`} style={{ width: `${(sn.count / max) * 100}%` }} />
                  <span className="absolute right-2 top-0.5 text-[10px] font-mono font-bold">{sn.count}x</span>
                </div>
                <span className="font-mono text-xs w-12 text-right font-bold">{sn.pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Block 4 — Last 20 Draws Pattern Comparison */}
      <div className="bg-white border rounded p-4">
        <h3 className="font-bold text-gray-900 mb-3">Block 4 — Last 20 Draws — Pattern Comparison</h3>
        <div className="overflow-x-auto" data-testid="recent-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-center">Draw</th>
                <th className="px-3 py-2 text-center">Date</th>
                <th className="px-3 py-2 text-center">Balls</th>
                <th className="px-3 py-2 text-center">Sum</th>
                <th className="px-3 py-2 text-center">Spread</th>
                <th className="px-3 py-2 text-center">Odds</th>
                <th className="px-3 py-2 text-center">Shared w/ Prev</th>
              </tr>
            </thead>
            <tbody>
              {(data.last_20 || []).map((d, idx) => (
                <tr key={d.draw} className={d.shared_with_prev >= 2 ? '' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={d.shared_with_prev >= 2 ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}}>
                  <td className="px-3 py-1.5 border-b font-mono text-center">#{d.draw}</td>
                  <td className="px-3 py-1.5 border-b text-center">{d.date}</td>
                  <td className="px-3 py-1.5 border-b font-mono text-center">{d.balls.join(', ')}</td>
                  <td className="px-3 py-1.5 border-b font-mono text-center">{d.sum}</td>
                  <td className="px-3 py-1.5 border-b font-mono text-center">{d.spread}</td>
                  <td className="px-3 py-1.5 border-b font-mono text-center">{d.odds}O</td>
                  <td className={`px-3 py-1.5 border-b font-mono text-center font-bold ${d.shared_with_prev >= 2 ? 'text-[#002FA7] bg-blue-50' : d.shared_with_prev === 0 ? 'text-gray-300' : ''}`}>{d.shared_with_prev}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Block 5 — Streak & Momentum Analysis */}
      {data.streak_analysis && <SharedStreakBlock sa={data.streak_analysis} />}
    </div>
  );
}

function SharedStreakBlock({ sa }) {
  const lstr = sa.longest_streaks || {};
  const nas = sa.next_after_shared || {};
  const hsa = sa.high_share_followup || {};
  const totalPairs = sa.total_pairs_analysed || 0;
  // For "heat-up" verdict: check if avg_next_shared trends upward as cur_shared increases
  const trend = [0, 1, 2, 3].map(c => nas[c]?.avg_next_shared).filter(v => v != null);
  const heatUp = trend.length >= 3 && trend[trend.length - 1] > trend[0] + 0.3;

  return (
    <div className="bg-white border-2 border-purple-200 rounded-xl overflow-hidden shadow-sm" data-testid="streak-block">
      <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-4 py-3">
        <h3 className="font-black text-base">Block 5 — Shared-Number Streak & Momentum (full history)</h3>
        <p className="text-[11px] text-white/85 mt-0.5">
          Does sharing many numbers in one draw "heat up" into the next? How long can the streak run? Across {totalPairs.toLocaleString()} consecutive-draw pairs.
        </p>
      </div>
      <div className="p-4 space-y-5">

        {/* Longest streak cards */}
        <div>
          <h4 className="text-xs font-black text-gray-700 mb-2 uppercase tracking-wider">Longest streaks ever recorded</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="longest-streaks">
            {[1, 2, 3, 4].map(thr => {
              const info = lstr[`shared_ge_${thr}`] || {};
              const streak = info.longest_streak || 0;
              return (
                <div key={thr} className={`p-3 rounded-lg border-2 ${streak === 0 ? 'bg-gray-50 border-gray-200' : streak >= 5 ? 'bg-emerald-50 border-emerald-300' : 'bg-blue-50 border-blue-200'}`} data-testid={`streak-ge-${thr}`}>
                  <div className="text-[10px] font-bold uppercase text-gray-500">Shared ≥ {thr}</div>
                  <div className={`text-3xl font-mono font-black mt-1 ${streak === 0 ? 'text-gray-300' : streak >= 5 ? 'text-emerald-700' : 'text-blue-700'}`}>
                    {streak}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">consecutive draws</div>
                  {streak > 0 && info.start_date && (
                    <div className="text-[10px] text-gray-600 mt-1.5 leading-tight">
                      {info.start_date}<br />→ {info.end_date}
                    </div>
                  )}
                  {streak === 0 && <div className="text-[10px] text-gray-400 mt-1 italic">Never happened</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Verdict on heat-up */}
        <div className={`rounded-lg p-3 border-2 ${heatUp ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`} data-testid="heat-verdict">
          <div className="flex items-start gap-2">
            <span className="text-xl">{heatUp ? '🔥' : '🧊'}</span>
            <div>
              <h4 className="font-black text-sm text-gray-900">
                {heatUp ? 'Numbers DO heat up — high share tends to continue' : 'Numbers DO NOT heat up — high share tends to cool down (regression to the mean)'}
              </h4>
              <p className="text-[11px] text-gray-700 mt-0.5">
                Avg next-draw shared count by current shared count:
                {[0, 1, 2, 3].map(c => nas[c] ? <span key={c} className="ml-2 inline-block bg-white px-2 py-0.5 rounded font-mono text-[10px] border border-gray-200">cur={c} → next≈{nas[c].avg_next_shared}</span> : null)}
              </p>
            </div>
          </div>
        </div>

        {/* Conditional next-shared distribution table */}
        <div>
          <h4 className="text-xs font-black text-gray-700 mb-2 uppercase tracking-wider">After N shared, what comes next?</h4>
          <div className="overflow-x-auto" data-testid="next-after-table">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase font-black text-gray-700 border border-gray-200">Current Shared</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase font-black text-gray-700 border border-gray-200">Cases</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase font-black text-gray-700 border border-gray-200">Avg next</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase font-black text-gray-700 border border-gray-200">Next = 0</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase font-black text-gray-700 border border-gray-200">Next = 1</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase font-black text-gray-700 border border-gray-200">Next = 2</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase font-black text-gray-700 border border-gray-200">Next = 3+</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3, 4, 5].map(cur => {
                  const info = nas[cur];
                  if (!info) return null;
                  const dist = info.distribution || [];
                  const pct = (n) => {
                    const row = dist.find(d => d.next_shared === n);
                    return row ? row.pct : 0;
                  };
                  const pct3plus = dist.filter(d => d.next_shared >= 3).reduce((s, d) => s + d.pct, 0);
                  return (
                    <tr key={cur} className={cur >= 3 ? 'bg-amber-50' : 'bg-white'} data-testid={`next-row-cur-${cur}`}>
                      <td className="px-3 py-2 font-mono font-black border border-gray-200">cur shared = <span className={cur >= 3 ? 'text-amber-700' : 'text-gray-900'}>{cur}</span></td>
                      <td className="px-3 py-2 font-mono text-center border border-gray-200">{info.occurrences}</td>
                      <td className="px-3 py-2 font-mono font-bold text-center border border-gray-200 text-purple-700">{info.avg_next_shared}</td>
                      {[0, 1, 2].map(n => {
                        const v = pct(n);
                        return <td key={n} className="px-3 py-2 font-mono text-center border border-gray-200">{v ? `${v}%` : '—'}</td>;
                      })}
                      <td className="px-3 py-2 font-mono text-center border border-gray-200 font-bold">{pct3plus ? `${pct3plus.toFixed(1)}%` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* High-share specific summary */}
        {hsa.cur_3_plus_count > 0 && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3" data-testid="high-share-followup">
            <h4 className="font-black text-sm text-amber-900 mb-1">⚡ When 3+ numbers DO repeat (rare event)</h4>
            <p className="text-[11px] text-amber-800 mb-2">
              In the entire history this has only happened <strong>{hsa.cur_3_plus_count}</strong> times.
              The very next draw averaged <strong>{hsa.avg_next_shared}</strong> shared with it — meaning the streak almost always breaks rather than continuing.
            </p>
            <div className="flex flex-wrap gap-2">
              {hsa.next_distribution.map(d => (
                <span key={d.next_shared} className="inline-flex items-center bg-white border border-amber-200 rounded px-2 py-1 text-[10px] font-mono">
                  next={d.next_shared} → <span className="font-bold text-amber-900 ml-1">{d.count}× ({d.pct}%)</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Current state */}
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-3 rounded-lg border-2 ${sa.current_streak_ge_1 >= 5 ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200'}`} data-testid="current-streak-1">
            <div className="text-[10px] font-bold uppercase text-gray-500">Current run of "1+ shared"</div>
            <div className="text-2xl font-mono font-black mt-1 text-emerald-700">{sa.current_streak_ge_1}</div>
            <div className="text-[10px] text-gray-500">consecutive draws</div>
          </div>
          <div className={`p-3 rounded-lg border-2 ${sa.current_streak_ge_2 >= 2 ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`} data-testid="current-streak-2">
            <div className="text-[10px] font-bold uppercase text-gray-500">Current run of "2+ shared"</div>
            <div className="text-2xl font-mono font-black mt-1 text-blue-700">{sa.current_streak_ge_2}</div>
            <div className="text-[10px] text-gray-500">consecutive draws</div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   PerPositionFrequencyTable — used in Same-Date Overview tab.
   Shows per-position frequency for numbers 1-50 (balls) + 1-12 (LS).
   Replaces former card-grid Anniversary blocks per Excel mock 2026-04-30.
   ============================================================ */
function PerPositionFrequencyTable({ title, subtitle, accent, ballRows, testId }) {
  const ballCols = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const lsCols = ['lucky_star_1', 'lucky_star_2'];
  const ballHeaders = ['B1', 'B2', 'B3', 'B4', 'B5'];
  const lsHeaders = ['LS1', 'LS2'];

  const renderCell = (val) => (
    val > 0
      ? <span className={`inline-block min-w-[1.5rem] text-center font-mono font-bold ${val >= 3 ? 'text-emerald-700' : val === 2 ? 'text-blue-700' : 'text-gray-700'}`}>{val}</span>
      : <span className="text-gray-300 font-mono">·</span>
  );

  return (
    <div className={`bg-gradient-to-br ${accent} rounded-lg overflow-hidden shadow-lg`} data-testid={testId}>
      <div className="p-4 text-white">
        <h3 className="font-bold text-lg leading-tight">{title}</h3>
        <p className="text-[11px] text-white/80 mt-0.5">{subtitle}</p>
      </div>
      <div className="bg-white">
        {/* Main balls table */}
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto" data-testid={`${testId}-balls`}>
          <table className="w-full text-xs">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-center text-[10px] uppercase font-black text-gray-700 sticky left-0 bg-gray-100">Number</th>
                {ballHeaders.map((h, i) => (
                  <th key={h} className="px-2 py-2 text-center text-[10px] uppercase font-black text-gray-700">{h}</th>
                ))}
                {lsHeaders.map((h, i) => (
                  <th key={h} className="px-2 py-2 text-center text-[10px] uppercase font-black text-amber-700 bg-amber-50">{h}</th>
                ))}
                <th className="px-2 py-2 text-center text-[10px] uppercase font-black text-[#002FA7]">Total</th>
              </tr>
            </thead>
            <tbody>
              {ballRows.map((r) => (
                <tr key={`b-${r.number}`} className={`${r.total >= 4 ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-gray-50'} border-b border-gray-100`}>
                  <td className="px-2 py-1 text-center font-mono font-black sticky left-0 bg-white">
                    {r.number}
                  </td>
                  {ballCols.map(c => <td key={c} className="px-2 py-1 text-center">{renderCell(r[c])}</td>)}
                  {lsCols.map(c => <td key={c} className="px-2 py-1 text-center bg-amber-50/40">{r.number <= 12 ? renderCell(r[c]) : <span className="text-gray-200 font-mono text-[9px]">N/A</span>}</td>)}
                  <td className={`px-2 py-1 text-center font-mono font-black ${r.total >= 4 ? 'text-emerald-700' : 'text-[#002FA7]'}`}>{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
